import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import PatientConsentBlock from "@/components/PatientConsentBlock";
import api, { apiErr } from "@/lib/api";
import { ArrowRightLeft, ArrowRight, Copy, Lock, Loader2, FileText, Mail, Phone, Printer, Plus, Pill, Tag, X as XIcon, Microscope, ShieldCheck, UserCircle, History, AlertTriangle, Trash2, StickyNote, LayoutTemplate, MapPin, Sparkles, Send, EyeOff, Undo2, ClipboardList, Glasses, Stethoscope, Scissors, Paperclip, ChevronsDown, ChevronsUp } from "lucide-react"; // eslint-disable-line no-unused-vars
import AiClinicalSuggest from "@/components/AiClinicalSuggest";
import DrugPrescriber from "@/components/DrugPrescriber";
import VisitDictation from "@/components/VisitDictation";
import PreviousVisitBlock from "@/components/PreviousVisitBlock";
import FseBlock from "@/components/FseBlock";
import VisitPreviewDrawer from "@/components/VisitPreviewDrawer";
import TopDrugsPanel from "@/components/TopDrugsPanel"; // eslint-disable-line no-unused-vars
import { templateForService, FIELD_LABELS, DIAGNOSTICA_OPTIONS, POSIZIONE_OPTIONS, TONO_METODI, ANNESSI_OPTIONS, PUPILLE_OPTIONS } from "@/lib/cartellaTemplates";
import MotilitaForm from "@/components/MotilitaForm";
import PrescrizioneOcchialiBlock from "@/components/PrescrizioneOcchialiBlock";
import CollapsibleSection from "@/components/CollapsibleSection";
import AnamnesiStructured, { deriveAnamnesiStructured } from "@/components/AnamnesiStructured";
import RefrazioneAvanzataBlock, { WheelSelect, SPHERE_OPTIONS, CYL_OPTIONS, AXIS_OPTIONS, ACUITY_OPTIONS } from "@/components/RefrazioneAvanzataBlock";
import ExamPresetChips from "@/components/ExamPresetChips";
import VisitSidebar from "@/components/VisitSidebar";
import SurgeryProgramBlock from "@/components/SurgeryProgramBlock";
import DiagnosticaPicker from "@/components/DiagnosticaPicker";
import AutoTextarea from "@/components/AutoTextarea";
import AiInlineField from "@/components/AiInlineField";
import ProtocolSuggestPanel from "@/components/ProtocolSuggestPanel";
import useVisitLive from "@/lib/useVisitLive";
import { SERVICES, serviceLabel } from "@/components/NewVisitModal";
import { toast } from "sonner";

function buildEyeFields(template) {
  return template.eye.map((k) => ({ k, label: FIELD_LABELS[k] || k }));
}

// Mappa service (ufficiali + legacy/ritirati) → chiave modello cartella canonica (in sync col backend)
const SERVICE_TEMPLATE_KEY_MAP = {
  base: "generale", visita: "generale", generale: "generale",
  glaucoma_visit: "glaucoma", glaucoma: "glaucoma",
  retina: "retina_medica", retina_medica: "retina_medica",
  cataratta: "cataratta", cornea: "refrattiva", cornea_rifrattiva: "refrattiva",
  pediatrica: "generale", pediatrico: "generale",
  ortottica: "ortottica", strabismo: "ortottica", strabismo_ortottica: "ortottica",
  refrattiva: "refrattiva", rifrattiva: "refrattiva", laser: "laser",
  fluorangiografia: "fluorangiografia", annessi: "generale",
  diabetica: "retina_medica", maculopatia: "retina_medica",
  oct: "retina_medica", chirurgia: "cataratta", postoperatorio: "postoperatorio", urgenze: "generale",
  glaucoma_chirurgia: "glaucoma_chirurgia",
};

// Merge default sani nei campi occhio: SOLO campi vuoti, mai visus
const VISUS_PROTECTED_KEYS = new Set([
  "visus_naturale", "visus_corretto", "visus_naturale_od", "visus_naturale_os",
  "visus_corretto_od", "visus_corretto_os",
  "visus_naturale_vicino", "visus_corretto_vicino", "visus_vicino",
]);
// === FASE 2: blocchi clinici compatti (Amsler, PV, Tono/Pachimetria) — mobile-safe ===

function EyeMiniPair({ title, accent, testid, children }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 mb-4" data-testid={testid}>
      <div className="text-xs font-extrabold uppercase tracking-wide mb-3" style={{ color: accent }}>{title}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

function AmslerEye({ side, data, set, disabled }) {
  const s = side.toLowerCase();
  const selected = (data.amsler_esito || "") === "positivo";
  const keptDesc = (data.amsler_note || data.amsler_tipo || "").trim();
  return (
    <div className={`rounded-lg border p-3 ${selected ? "border-rose-300 bg-rose-50/30" : "border-slate-100"}`}>
      <button type="button" disabled={disabled} data-testid={`amsler-${s}-toggle`}
        onClick={() => set((p) => ({ ...p, amsler_esito: selected ? "" : "positivo" }))}
        className="flex items-center gap-2 w-full text-left disabled:opacity-60">
        <span className={`w-5 h-5 rounded border-2 flex items-center justify-center text-[13px] font-extrabold leading-none ${selected ? "bg-rose-600 border-rose-600 text-white" : "bg-white border-slate-300 text-transparent"}`}>✕</span>
        <span className="text-[11px] font-extrabold text-brand-navy">{side}</span>
        <span className="text-[10px] text-slate-500">{selected ? "Alterazioni presenti (X)" : "Seleziona se alterato"}</span>
      </button>
      {selected && (
        <input disabled={disabled} data-testid={`amsler-${s}-note`} value={data.amsler_note || ""}
          onChange={(e) => set((p) => ({ ...p, amsler_note: e.target.value }))}
          placeholder="Descrizione facoltativa (metamorfopsie, scotoma…)"
          className="mt-2 w-full px-2 py-1.5 rounded-lg border border-rose-200 bg-white text-xs focus:outline-none focus:border-rose-400 disabled:bg-slate-50" />
      )}
      {!selected && keptDesc && (
        <div className="mt-1.5 text-[10px] text-slate-400 italic" data-testid={`amsler-${s}-desc-kept`}>Descrizione conservata: {keptDesc}</div>
      )}
      {data.amsler_esito === "negativo" && (
        <div className="mt-1.5 text-[10px] text-emerald-700" data-testid={`amsler-${s}-legacy-neg`}>Dato storico: nella norma (−)</div>
      )}
    </div>
  );
}

function PvLiberoEye({ side, data, set, disabled }) {
  const s = side.toLowerCase();
  return (
    <div className="rounded-lg border border-slate-100 p-3">
      <div className="text-[11px] font-extrabold text-brand-navy mb-2">{side}</div>
      <input disabled={disabled} data-testid={`visus-vicino-${s}`} value={data.visus_vicino || ""}
        onChange={(e) => set((p) => ({ ...p, visus_vicino: e.target.value }))}
        placeholder="es. J1 a 35 cm"
        className="w-full px-2 py-2 rounded-lg border border-[#E2E8F0] text-xs focus:outline-none focus:border-brand-azure disabled:bg-slate-50" />
    </div>
  );
}

function TonoPachiEye({ side, data, set, disabled }) {
  const s = side.toLowerCase();
  const metodo = data.tono_metodo || "";
  return (
    <div className="rounded-lg border border-slate-100 p-3 space-y-2.5">
      <div className="text-[11px] font-extrabold text-brand-navy">{side}</div>
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Tono <span className="normal-case">(mmHg)</span></div>
        <div className="flex gap-1.5 items-center flex-wrap">
          <input data-testid={`eye-${s}-tono`} disabled={disabled} value={data.tono || ""} inputMode="decimal"
            onChange={(e) => set((p) => ({ ...p, tono: e.target.value }))} placeholder="es. 14"
            className="w-20 px-2 py-1.5 rounded-lg border border-[#E2E8F0] text-xs focus:outline-none focus:border-brand-azure disabled:bg-slate-50 disabled:text-slate-500" />
          <div className="flex gap-1 flex-wrap" data-testid={`eye-${s}-tono-metodo`}>
            {TONO_METODI.map((m) => {
              const on = metodo === m.value;
              return (
                <button key={m.value} type="button" tabIndex={-1} disabled={disabled}
                  data-testid={`eye-${s}-tono-metodo-${m.value}`}
                  onClick={() => set((p) => ({ ...p, tono_metodo: on ? "" : m.value }))}
                  className={`px-2 py-1 rounded-lg text-[10px] font-bold border-2 transition ${on ? "bg-brand-azure text-white border-brand-azure" : "bg-white text-slate-600 border-[#E2E8F0] hover:border-brand-azure hover:text-brand-azure"} disabled:opacity-60`}>
                  {m.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Pachimetria <span className="normal-case">(µm)</span></div>
        <input data-testid={`eye-${s}-pachimetria`} disabled={disabled} value={data.pachimetria || ""} inputMode="decimal"
          onChange={(e) => set((p) => ({ ...p, pachimetria: e.target.value }))} placeholder="es. 540"
          className="w-24 px-2 py-1.5 rounded-lg border border-[#E2E8F0] text-xs focus:outline-none focus:border-brand-azure disabled:bg-slate-50 disabled:text-slate-500" />
      </div>
      <div data-testid={`eye-${s}-tono-corretto`}>
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Tono corretto <span className="normal-case">(mmHg)</span></div>
        <div className="text-xs font-bold text-brand-navy">
          {data.tono_corretto ? (
            <>
              {data.tono_corretto} mmHg <span className="ml-1 text-[10px] font-normal text-slate-400">stima indicativa</span>
            </>
          ) : "—"}
        </div>
      </div>
    </div>
  );
}

function mergeEmptyEyeFields(cur, def) {
  const out = { ...(cur || {}) };
  Object.entries(def || {}).forEach(([k, val]) => {
    if (VISUS_PROTECTED_KEYS.has(k)) return;
    const existing = out[k];
    const isEmpty = existing === undefined || existing === null || (typeof existing === "string" && !existing.trim());
    if (val && isEmpty) out[k] = val;
  });
  return out;
}

const TEXTAREA_FIELDS = ["note", "segmento_anteriore", "fundus", "oct_macula", "oct_nervo", "angio_oct", "campo_visivo", "topografia", "tomografia", "biometria", "lancaster", "note_genitori", "parametri_laser"];

// Se un valore legacy non è tra le opzioni del selettore, viene aggiunto in testa
// così resta visibile e selezionato (mai nascondere dati esistenti).
const withValue = (options, v) => (v && !options.includes(v) ? [v, ...options] : options);

/**
 * Griglia OD | OS unificata: per ogni campo una riga [etichetta | OD | OS | copia→].
 * TAB orizzontale nativo (DOM row-major: OD → OS → campo successivo); i pulsanti
 * copia hanno tabIndex=-1 e NON entrano nel tab order. Raggruppamento anatomico EO
 * (Segmento anteriore/posteriore) con campi generici legacy visibili in nota ambra.
 */
function EyePairForm({ fields, OD, OS, setOD, setOS, readOnly, isEyeExcluded, eyeExcluded = {}, onCopyAll, onCopyField, idSuffix = "" }) {
  const eyes = [
    { side: "OD", data: OD, set: setOD },
    { side: "OS", data: OS, set: setOS },
  ];
  const SA_KEYS = ["gonioscopia", "film_lacrimale", "superficie_oculare", "congiuntiva", "cornea", "camera_anteriore", "iride", "pupille", "cristallino"];
  const SP_KEYS = ["vitreo"];
  const RETINA_KEYS = ["papilla", "macula", "vasi", "retina_periferica", "periferia_retinica"];
  const LEGACY_HIDDEN = ["segmento_anteriore", "fundus", "fondo_oculare"];
  const EO_PH = {
    congiuntiva: "Congiuntiva rosea e lucida", cornea: "Trasparente, regolare",
    camera_anteriore: "Formata, otticamente vuota", iride: "Trofica, regolare",
    pupille: "Isocorica, normoreagente", cristallino: "Trasparente, in sede (nucleo/corticale)",
    papilla: "Papilla rosea a margini netti", macula: "Macula indenne, riflesso foveale presente",
    vasi: "Vasi di calibro e decorso regolari", periferia_retinica: "Retina piana, assenza di lesioni regmatogene",
    retina_periferica: "Retina piana, assenza di lesioni regmatogene",
    gonioscopia: "Angolo aperto, gradazione regolare", film_lacrimale: "BUT nella norma",
    superficie_oculare: "Assenza di staining",
    orbita: "Nella norma", vitreo: "Trasparente",
    annessi_palpebre: "Annessi e palpebre nella norma, ciglia regolari…",
  };

  const cell = (f, { side, data, set }) => {
    const sideL = side.toLowerCase();
    const disabled = readOnly || isEyeExcluded(side);
    const value = data[f.k] || "";
    if (f.k === "visus_naturale" || f.k === "visus_corretto") {
      const vk = `${f.k}_vicino`;
      return (
        <div className="grid grid-cols-2 gap-1" data-testid={`eye-${sideL}-${f.k}-wrap`}>
          <div>
            <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Lontano</div>
            <WheelSelect value={value} options={withValue(ACUITY_OPTIONS, value)} disabled={disabled}
              testid={`eye-${sideL}-${f.k}`} onChange={(val) => set((p) => ({ ...p, [f.k]: val }))} />
          </div>
          <div>
            <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">Vicino</div>
            <input disabled={disabled} data-testid={`eye-${sideL}-${vk}`} value={data[vk] || ""}
              onChange={(e) => set((p) => ({ ...p, [vk]: e.target.value }))} placeholder="es. J1"
              className="w-full px-2 py-1.5 rounded-lg border border-[#E2E8F0] text-xs focus:outline-none focus:border-brand-azure disabled:bg-slate-50 disabled:text-slate-500" />
          </div>
        </div>
      );
    }
    if (f.k === "posizione_refrazione") {
      const isAltro = value === "altro";
      return (
        <div>
          <select data-testid={`eye-${sideL}-${f.k}`} disabled={disabled} value={value || "primaria"}
            onChange={(e) => {
              const val = e.target.value;
              set((p) => {
                const next = { ...p, [f.k]: val };
                if (val !== "altro") next.posizione_refrazione_altro = "";
                return next;
              });
            }}
            className="w-full px-2 py-1.5 rounded-lg border border-[#E2E8F0] focus:outline-none focus:border-brand-azure disabled:bg-slate-50 disabled:text-slate-500 text-xs bg-white">
            {POSIZIONE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {isAltro && (
            <input data-testid={`eye-${sideL}-${f.k}-altro`} disabled={disabled} value={data.posizione_refrazione_altro || ""}
              onChange={(e) => set({ ...data, posizione_refrazione_altro: e.target.value })}
              placeholder="Specifica posizione…"
              className="mt-1 w-full px-2 py-1.5 rounded-lg border border-brand-orange/40 bg-amber-50 focus:outline-none focus:border-brand-orange disabled:bg-slate-50 text-xs" />
          )}
        </div>
      );
    }
    if (f.k === "autoref_no_ciclo" || f.k === "autoref_ciclo") {
      const p = f.k;
      return (
        <div className="grid grid-cols-3 gap-1" data-testid={`eye-${sideL}-${f.k}`}>
          <WheelSelect value={data[`${p}_sph`] || ""} options={withValue(SPHERE_OPTIONS, data[`${p}_sph`])} disabled={disabled}
            testid={`eye-${sideL}-${f.k}-sph`} ph="Sfera" onChange={(val) => set((prev) => ({ ...prev, [`${p}_sph`]: val }))} />
          <WheelSelect value={data[`${p}_cyl`] || ""} options={withValue(CYL_OPTIONS, data[`${p}_cyl`])} disabled={disabled}
            testid={`eye-${sideL}-${f.k}-cyl`} ph="Cil." onChange={(val) => set((prev) => ({ ...prev, [`${p}_cyl`]: val }))} />
          <WheelSelect value={data[`${p}_axis`] || ""} options={withValue(AXIS_OPTIONS, data[`${p}_axis`])} disabled={disabled}
            testid={`eye-${sideL}-${f.k}-axis`} ph="Asse" onChange={(val) => set((prev) => ({ ...prev, [`${p}_axis`]: val }))} />
        </div>
      );
    }
    if (f.k === "tono") {
      const metodo = data.tono_metodo || "";
      return (
        <div className="flex gap-1.5 items-center flex-wrap">
          <input data-testid={`eye-${sideL}-${f.k}`} disabled={disabled} value={value}
            onChange={(e) => { const val = e.target.value; set((p) => ({ ...p, [f.k]: val })); }} placeholder="es. 12"
            className="w-16 px-2 py-1.5 rounded-lg border border-[#E2E8F0] focus:outline-none focus:border-brand-azure disabled:bg-slate-50 disabled:text-slate-500 text-xs" />
          <span className="text-[10px] text-slate-500 font-bold select-none" data-testid={`eye-${sideL}-tono-unit`}>mmHg</span>
          <div className="flex gap-1 flex-wrap" data-testid={`eye-${sideL}-tono-metodo`}>
            {TONO_METODI.map((m) => {
              const on = metodo === m.value;
              return (
                <button key={m.value} type="button" tabIndex={-1} disabled={disabled}
                  data-testid={`eye-${sideL}-tono-metodo-${m.value}`}
                  onClick={() => set((p) => ({ ...p, tono_metodo: on ? "" : m.value }))}
                  className={`px-2 py-1 rounded-lg text-[10px] font-bold border-2 transition ${on ? "bg-brand-azure text-white border-brand-azure" : "bg-white text-slate-600 border-[#E2E8F0] hover:border-brand-azure hover:text-brand-azure"} disabled:opacity-60`}>
                  {m.label}
                </button>
              );
            })}
          </div>
        </div>
      );
    }
    const isTa = TEXTAREA_FIELDS.includes(f.k) || f.k === "annessi_palpebre" || f.k === "pupille" || SA_KEYS.includes(f.k) || SP_KEYS.includes(f.k) || RETINA_KEYS.includes(f.k);
    if (isTa) {
      return (
        <textarea data-testid={`eye-${sideL}-${f.k}`} disabled={disabled} rows={2} value={value}
          onChange={(e) => { const val = e.target.value; set((p) => ({ ...p, [f.k]: val })); }} placeholder={EO_PH[f.k] || ""}
          style={{ lineHeight: "1.55" }}
          className="w-full px-2.5 py-1.5 rounded-lg border border-[#E2E8F0] focus:outline-none focus:border-brand-azure disabled:bg-slate-50 disabled:text-slate-500 text-xs leading-relaxed resize-none break-words whitespace-pre-wrap" />
      );
    }
    return (
      <input data-testid={`eye-${sideL}-${f.k}`} disabled={disabled} value={value}
        onChange={(e) => { const val = e.target.value; set((p) => ({ ...p, [f.k]: val })); }} placeholder={EO_PH[f.k] || ""}
        className="w-full px-2.5 py-1.5 rounded-lg border border-[#E2E8F0] focus:outline-none focus:border-brand-azure disabled:bg-slate-50 disabled:text-slate-500 text-xs" />
    );
  };

  const row = (f) => (
    <React.Fragment key={f.k}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 pt-2 pr-1">{f.label}</div>
      {eyes.map((e) => (
        <div key={e.side} className={isEyeExcluded(e.side) ? "opacity-50" : ""}>{cell(f, e)}</div>
      ))}
      <div className="pt-1 text-center">
        {!readOnly && !isEyeExcluded("OS") && (
          <button type="button" tabIndex={-1} data-testid={`copy-field-od-${f.k}`}
            onClick={() => onCopyField("OD", f.k)} title="Copia OD → OS"
            className="text-slate-400 hover:text-brand-azure hover:bg-brand-azure/10 rounded p-1">
            <ArrowRight size={13} />
          </button>
        )}
      </div>
    </React.Fragment>
  );

  const fullRow = (content, key) => (
    <div key={key} style={{ gridColumn: "1 / -1" }}>{content}</div>
  );

  const byKey = Object.fromEntries(fields.map((f) => [f.k, f]));
  const isEO = fields.some((f) => SA_KEYS.includes(f.k) || RETINA_KEYS.includes(f.k));
  const grouped = new Set([...SA_KEYS, ...SP_KEYS, ...RETINA_KEYS, ...LEGACY_HIDDEN, "note"]);
  const head = isEO ? fields.filter((f) => !grouped.has(f.k)) : fields.filter((f) => !LEGACY_HIDDEN.includes(f.k));
  const sa = isEO ? SA_KEYS.map((k) => byKey[k]).filter(Boolean) : [];
  const sp = isEO ? SP_KEYS.map((k) => byKey[k]).filter(Boolean) : [];
  const retina = isEO ? RETINA_KEYS.map((k) => byKey[k]).filter(Boolean) : [];
  // Template legacy senza nuovi campi EO: mostra anche i campi generici come righe normali.
  const legacyFieldRows = !isEO ? fields.filter((f) => LEGACY_HIDDEN.includes(f.k)) : [];
  const noteF = isEO ? byKey.note : null;
  const legacyBoxes = eyes.map(({ side, data }) => ({
    side,
    texts: isEO ? LEGACY_HIDDEN.map((k) => (data[k] || "").trim() && { k, txt: data[k] }).filter(Boolean) : [],
  })).filter((b) => b.texts.length > 0);
  const Header = ({ children, tid }) => fullRow(
    <div className="pt-2 mt-1 border-t border-slate-200 text-[11px] font-extrabold uppercase tracking-widest text-brand-navy" data-testid={tid}>{children}</div>, tid
  );

  return (
    <div className="bg-white rounded-xl border border-[#E2E8F0] p-4 mb-4" data-testid={`eye-pair-form${idSuffix}`}>
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          {eyes.map(({ side }) => {
            const exc = isEyeExcluded(side);
            return (
              <div key={side} className="flex items-center gap-1.5">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center font-extrabold text-white text-xs ${side === "OD" ? "bg-brand-navy" : "bg-brand-azure"}`}>{side}</div>
                <span className="text-xs font-bold text-brand-navy">{side === "OD" ? "Occhio Destro" : "Occhio Sinistro"}</span>
                {exc && (
                  <span className="text-[9px] uppercase tracking-wider bg-slate-700 text-white px-2 py-0.5 rounded font-bold" data-testid={`eye-excluded-badge-${side.toLowerCase()}${idSuffix}`} title={(eyeExcluded[side] || {}).reason || ""}>
                    Escluso / N.A.
                  </span>
                )}
              </div>
            );
          })}
        </div>
        {!readOnly && !isEyeExcluded("OD") && !isEyeExcluded("OS") && (
          <button type="button" tabIndex={-1} data-testid="copy-all-od" onClick={() => onCopyAll("OD")}
            className="text-xs font-bold bg-brand-azure/10 text-brand-azure hover:bg-brand-azure/20 px-2.5 py-1.5 rounded-lg flex items-center gap-1">
            <ArrowRight size={13} /> Copia tutto OD → OS
          </button>
        )}
      </div>
      <div className="grid gap-x-2 gap-y-2 items-start" style={{ gridTemplateColumns: "minmax(110px, 170px) 1fr 1fr 30px" }}>
        <div></div>
        {eyes.map(({ side }) => (
          <div key={side} className={`text-center text-[10px] font-extrabold uppercase tracking-wider ${side === "OD" ? "text-brand-navy" : "text-brand-azure"}`}>{side}</div>
        ))}
        <div></div>
        {head.map(row)}
        {sa.length > 0 && <Header tid="eo-header-sa">Segmento anteriore</Header>}
        {sa.map(row)}
        {(sp.length > 0 || retina.length > 0) && <Header tid="eo-header-sp">Segmento posteriore</Header>}
        {sp.map(row)}
        {retina.length > 0 && fullRow(
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 pl-2 border-l-2 border-brand-azure/40" data-testid="eo-header-retina">Retina / Fondo oculare</div>, "retina-h"
        )}
        {retina.map(row)}
        {legacyFieldRows.map(row)}
        {legacyBoxes.map((b) => fullRow(
          <div className="text-[10px] text-slate-600 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid={`eo-legacy-${b.side.toLowerCase()}`}>
            <b>{b.side} — Dati precedenti (campi generici):</b>
            {b.texts.map(({ k, txt }) => <div key={k}>{{ segmento_anteriore: "Segmento anteriore", fundus: "Fundus", fondo_oculare: "Fondo oculare" }[k] || k}: {txt}</div>)}
          </div>, `legacy-${b.side}`
        ))}
        {noteF && row(noteF)}
      </div>
    </div>
  );
}

function TerapiaItem({ item, idx, onRemove, readOnly }) {
  const lines = [];
  if (item.dose) lines.push(item.dose);
  if (item.frequency_per_day) lines.push(`${item.frequency_per_day} volte/die`);
  if (item.days) lines.push(`${item.days} giorni`);
  const sched = lines.join(" · ");
  return (
    <li className="border border-[#E2E8F0] rounded-lg p-3" data-testid={`terapia-item-${idx}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-brand-navy">{item.brand}</span>
            {item.verified ? (
              <span className="text-[9px] uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-bold">RCP</span>
            ) : (
              <span className="text-[9px] uppercase tracking-wider bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-bold">demo</span>
            )}
          </div>
          <div className="text-xs text-slate-600 mt-0.5"><b>{item.principle}</b> · {item.form}</div>
          {sched && <div className="text-xs text-brand-navy mt-1"><b>Schema:</b> {sched}</div>}
          {item.route && <div className="text-[11px] text-slate-500">Via: {item.route}</div>}
          {item.instructions && <div className="text-xs text-slate-700 mt-1 whitespace-pre-wrap">{item.instructions}</div>}
          {item.extra_notes && <div className="text-[11px] text-slate-500 italic mt-1">Nota: {item.extra_notes}</div>}
          {item.contraindications_ref && (
            <details className="mt-1.5">
              <summary className="text-[10px] uppercase tracking-wider text-red-700 cursor-pointer hover:underline">Controindicazioni</summary>
              <div className="text-[11px] text-red-900 leading-relaxed mt-1 bg-red-50 border border-red-200 rounded p-2">{item.contraindications_ref}</div>
            </details>
          )}
          <div className="text-[10px] text-slate-400 mt-1.5">{item.category} · ATC {item.drug_atc}{item.source ? ` · fonte: ${item.source}` : ""}</div>
        </div>
        {!readOnly && (
          <button data-testid={`terapia-remove-${idx}`} onClick={() => onRemove(idx)} className="text-slate-400 hover:text-red-600 p-1"><Trash2 size={14} /></button>
        )}
      </div>
    </li>
  );
}

export default function Visita() {
  const { vid } = useParams();
  const nav = useNavigate();
  const [v, setV] = useState(null);
  const [anagrafica, setAnagrafica] = useState({ patient_name: "", patient_surname: "", patient_birth: "", patient_fiscal_code: "", patient_email: "", patient_phone: "", patient_address: "", patient_city: "", patient_cap: "", patient_province: "", privacy_contact_consent: false });
  const [doctorNote, setDoctorNote] = useState("");
  const [doctorNoteMeta, setDoctorNoteMeta] = useState({ updated_at: null });
  const [doctorNoteSaving, setDoctorNoteSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [anamnesi, setAnamnesi] = useState("");
  const [OD, setOD] = useState({});
  const [OS, setOS] = useState({});
  const [diagnosi, setDiagnosi] = useState("");
  const [terapia, setTerapia] = useState("");
  const [terapiaItems, setTerapiaItems] = useState([]);
  const [prossimoControllo, setProssimoControllo] = useState("");
  const [referto, setReferto] = useState("");
  const [tags, setTags] = useState([]);
  const [behavioralProtocol, setBehavioralProtocol] = useState("");
  const [clinicalHashtags, setClinicalHashtags] = useState([]);
  const [protocolTemplatesByTag, setProtocolTemplatesByTag] = useState({}); // {hashtag: template}
  const [hashtagInputValue, setHashtagInputValue] = useState("");
  const [healthyDefaults, setHealthyDefaults] = useState(null); // {ana
  const [pendingService, setPendingService] = useState(null); // conferma inline cambio modello
  const [iopCfg, setIopCfg] = useState(null); // parametri versionati tono corretto da pachimetria
  // Fase 31B: suggerimento IA di aggiornamento anamnesi master (mai auto-applicato)
  const [anamnesiSuggested, setAnamnesiSuggested] = useState("");
  const [anamnesiSuggestSaving, setAnamnesiSuggestSaving] = useState(false);
  const [diagnostica, setDiagnostica] = useState([]);
  const [phase, setPhase] = useState("previsita");
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [drugOpen, setDrugOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [closing, setClosing] = useState(false);
  const [addendumText, setAddendumText] = useState("");
  const [sendOpen, setSendOpen] = useState(false);
  // Azioni distruttive: stato del modal di conferma minimale
  const [destructiveAction, setDestructiveAction] = useState(null); // "delete" | "archive" | "restore"
  const [destructiveBusy, setDestructiveBusy] = useState(false);
  // Lock 60min countdown + edit mode dopo chiusura
  const [editStatus, setEditStatus] = useState(null); // {editable, minutes_remaining, locked, reason}
  const [editReportMode, setEditReportMode] = useState(false);
  const [savingReportEdit, setSavingReportEdit] = useState(false);
  const [editReason, setEditReason] = useState("");
  // Anteprima storico paziente
  const [previewVid, setPreviewVid] = useState(null);
  // Template ambulatorio: lista di blocchi attivi per la visita corrente
  const [templateBlocks, setTemplateBlocks] = useState(null); // null = mostra tutto (fallback)
  const [templateDiag, setTemplateDiag] = useState([]);
  const [diagPickerOpen, setDiagPickerOpen] = useState(false);
  // Diagnostica: referti testuali + allegati per ciascun esame
  const [diagnosticaReferti, setDiagnosticaReferti] = useState({});
  const [diagnosticaAllegati, setDiagnosticaAllegati] = useState({});
  const [specialized, setSpecialized] = useState({});
  const [prescrizioneOcchiali, setPrescrizioneOcchiali] = useState({});
  // Consenso unico paziente
  const [consensoUnico, setConsensoUnico] = useState(false);
  const [consensoAcquiredAt, setConsensoAcquiredAt] = useState(null);
  // Anamnesi master paziente (per importare/aggiornare)
  const [anamnesiMaster, setAnamnesiMaster] = useState({ anamnesi: "", updated_at: null, source: null });
  const [anamnesiSource, setAnamnesiSource] = useState(null);
  const [anamnesiSaving, setAnamnesiSaving] = useState(false);
  const [ouCustom, setOuCustom] = useState({});
  // Fase B/C: anamnesi strutturata a toggle + esclusione occhio (additivi, retro-compatibili)
  const [anamnesiStructured, setAnamnesiStructured] = useState({});
  const [eyeExcluded, setEyeExcluded] = useState({});
  // Fase A: gating SMS se non configurato
  const [msgConfig, setMsgConfig] = useState(null);
  // Sezioni richiudibili
  const [openSections, setOpenSections] = useState({ anamnesi: true, refrazione: true, esame: true, diagnosi: true, chirurgia: true, allegati: true });
  const toggleSection = (id) => setOpenSections((p) => ({ ...p, [id]: !p[id] }));
  const setAllSections = (v) => setOpenSections({ anamnesi: v, refrazione: v, esame: v, diagnosi: v, chirurgia: v, allegati: v });
  // Undo ultimo inserimento IA
  const [aiSnapshot, setAiSnapshot] = useState(null);
  // Proposta terapia in corso da anamnesi
  const [terapiaProposalDismissed, setTerapiaProposalDismissed] = useState(false);
  // Render singolo della colonna destra (evita data-testid duplicati mobile/desktop)
  const [isXl, setIsXl] = useState(() => typeof window !== "undefined" && window.innerWidth >= 1280);
  useEffect(() => {
    const onResize = () => setIsXl(window.innerWidth >= 1280);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const onAttachDiag = (dId, file) => {
    if (!file) return;
    setDiagnosticaAllegati((p) => {
      const cur = p[dId] || [];
      return { ...p, [dId]: [...cur, { name: file.name, size: file.size, type: file.type }] };
    });
    // Upload reale richiede object storage backend — placeholder che traccia solo metadati per ora
    toast.info(`Allegato "${file.name}" registrato. Upload backend in arrivo.`);
  };
  // Gaze9 foto upload: usa lo stesso pipeline diagnostiche, sotto la chiave "diag_gaze9_foto"
  const onAttachGaze9 = (file) => {
    if (!file) return;
    setDiagnosticaAllegati((p) => {
      const cur = p["diag_gaze9_foto"] || [];
      return { ...p, diag_gaze9_foto: [...cur, { name: file.name, size: file.size, type: file.type, kind: "gaze9" }] };
    });
    // Se non era già nelle diagnostiche, aggiungilo per renderlo visibile nel PDF/referto
    setDiagnostica((p) => p.includes("diag_gaze9_foto") ? p : [...p, "diag_gaze9_foto"]);
    toast.success(`Foto Gaze9 "${file.name}" registrata`);
  };
  // Anti-perdita dati: dirty tracking + autosave + modal conferma uscita
  const [dirty, setDirty] = useState(false);
  // Sedi del medico
  const [sites, setSites] = useState([]);
  const [sedeId, setSedeId] = useState("");
  // Tag persistenti del paziente (collezione medico_patient_tags)
  const [patientTags, setPatientTags] = useState([]);
  const [patientTagsUpdatedAt, setPatientTagsUpdatedAt] = useState(null);
  const [patientTagSuggestions, setPatientTagSuggestions] = useState([]);
  const [patientTagInput, setPatientTagInput] = useState("");
  const [patientTagsSaving, setPatientTagsSaving] = useState(false);
  const [autosaveStatus, setAutosaveStatus] = useState("idle"); // idle | saving | saved | error
  const [autosaveAt, setAutosaveAt] = useState(null);
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);
  const [pendingExitAction, setPendingExitAction] = useState(null); // () => void
  const initialLoadedRef = React.useRef(false);
  const autoDefaultsAppliedRef = React.useRef({});
  const autosaveTimerRef = React.useRef(null);
  const autosaveDraftRef = React.useRef(null);

  // === REAL-TIME LIVE SYNC (multi-postazione stesso medico) ===
  const applyRemoteVisit = React.useCallback((remote) => {
    if (!remote) return;
    // Allinea campi clinici principali (no anagrafica per non sovrascrivere durante editing parallelo)
    setOD(remote.OD || {});
    setOS(remote.OS || {});
    setAnamnesi(remote.anamnesi || "");
    setDiagnosi(remote.diagnosi || "");
    setTerapia(remote.terapia || "");
    setReferto(remote.referto || "");
    setTags(remote.tags || []);
    setProssimoControllo(remote.prossimo_controllo || "");
    setBehavioralProtocol(remote.behavioral_protocol || "");
    setClinicalHashtags(Array.isArray(remote.clinical_hashtags) ? remote.clinical_hashtags : []);
    setSpecialized(remote.specialized || {});
    setAnamnesiStructured(remote.anamnesi_structured || {});
    setEyeExcluded(remote.eye_excluded || {});
    setPrescrizioneOcchiali(remote.prescrizione_occhiali || {});
    setDiagnostica((remote.diagnostica || []).map(d => (typeof d === "string" ? d : d.id)).filter(Boolean));
    setDiagnosticaReferti(remote.diagnostica_referti || {});
    setDiagnosticaAllegati(remote.diagnostica_allegati || {});
    setTerapiaItems(remote.terapia_items || []);
    setSedeId(remote.sede_id || "");
    setV(remote);
    // dopo apply non considerare dirty
    setTimeout(() => setDirty(false), 50);
    toast.info("Visita aggiornata da un'altra postazione", { duration: 4000 });
  }, []);

  // Live sync hook (presence + poll updated_at). Workstation: usa nome ruolo se disponibile, altrimenti generico
  const liveWorkstation = (typeof window !== "undefined" && window.localStorage.getItem("pv-workstation")) || "Postazione";
  const lastLocalSaveRef = React.useRef(0);
  const { activeSessions, lastSync, conflict, dismissConflict } = useVisitLive({
    vid,
    currentUpdatedAt: v?.updated_at,
    isDirty: dirty,
    getLastLocalSaveAt: () => lastLocalSaveRef.current,
    onRemoteUpdate: (remote) => {
      // Guardia anti-dataloss: mai applicare uno snapshot remoto mentre l'utente sta
      // digitando in un campo (verrà riproposto al tick successivo se ancora rilevante).
      const ae = document.activeElement;
      if (ae && ["INPUT", "TEXTAREA", "SELECT"].includes(ae.tagName)) return;
      applyRemoteVisit(remote);
    },
    workstation: liveWorkstation,
  });

  // Marca dirty su ogni cambio dei campi editabili
  useEffect(() => {
    if (!initialLoadedRef.current) return;
    setDirty(true);
  }, [anamnesi, OD, OS, diagnosi, terapia, referto, tags, behavioralProtocol, clinicalHashtags, diagnostica, diagnosticaReferti, diagnosticaAllegati, specialized, prescrizioneOcchiali, consensoUnico, terapiaItems, anagrafica, privacyConsent, phase, sedeId, prossimoControllo, anamnesiStructured, eyeExcluded]);

  // Anti-swipe back: blocca popstate + beforeunload quando ci sono modifiche non salvate
  useEffect(() => {
    if (!vid) return;
    // CSS overscroll-behavior per bloccare swipe-back trackpad
    document.documentElement.classList.add("pv-no-swipe");
    // Push fake state per intercettare back gesture/key
    window.history.pushState({ pvGuard: true, vid }, "");
    const onPopState = (e) => {
      if (dirty) {
        // Reinserisci lo state per restare sulla pagina
        window.history.pushState({ pvGuard: true, vid }, "");
        setPendingExitAction(() => () => nav(-2));
        setExitConfirmOpen(true);
      }
    };
    const onBeforeUnload = (e) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = "Hai modifiche non salvate nella cartella clinica. Sei sicuro di voler uscire?";
        return e.returnValue;
      }
    };
    window.addEventListener("popstate", onPopState);
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("popstate", onPopState);
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.documentElement.classList.remove("pv-no-swipe");
    };
    // eslint-disable-next-line
  }, [vid, dirty]);

  // Debounced autosave su ogni dirty change (5s) — DEVE stare prima dell'early return per rispettare rules-of-hooks
  useEffect(() => {
    if (!dirty || !v || v.status === "closed") return;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => { autosaveDraftRef.current && autosaveDraftRef.current(); }, 5000);
    return () => { if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current); };
    // eslint-disable-next-line
  }, [dirty, anamnesi, OD, OS, diagnosi, terapia, referto, tags, behavioralProtocol, terapiaItems, v]);

  const refreshEditStatus = async (visitId) => {
    try {
      const { data } = await api.get(`/visits/${visitId}/editable-status`);
      setEditStatus(data);
    } catch { setEditStatus(null); }
  };

  // Polling ogni 30s quando in finestra di modifica
  useEffect(() => {
    if (!vid || !editStatus?.editable || editStatus?.reason === "draft") return;
    const t = setInterval(() => refreshEditStatus(vid), 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [vid, editStatus?.editable, editStatus?.reason]);

  const loadDoctorNote = async (patient) => {
    try {
      const params = {};
      if (patient.patient_fiscal_code) params.fiscal_code = patient.patient_fiscal_code;
      else { params.name = patient.patient_name; params.surname = patient.patient_surname; if (patient.patient_birth) params.birth = patient.patient_birth; }
      const { data } = await api.get("/medico/patient-notes", { params });
      setDoctorNote(data.note || "");
      setDoctorNoteMeta({ updated_at: data.updated_at });
    } catch { setDoctorNote(""); setDoctorNoteMeta({ updated_at: null }); }
  };

  const saveDoctorNote = async (next) => {
    setDoctorNoteSaving(true);
    try {
      const params = {};
      if (anagrafica.patient_fiscal_code) params.fiscal_code = anagrafica.patient_fiscal_code;
      else { params.name = anagrafica.patient_name; params.surname = anagrafica.patient_surname; if (anagrafica.patient_birth) params.birth = anagrafica.patient_birth; }
      const { data } = await api.put("/medico/patient-notes", { ...params, note: next });
      setDoctorNoteMeta({ updated_at: data.updated_at });
      toast.success("Nota personale aggiornata");
    } catch (e) { toast.error(apiErr(e, "Errore salvataggio nota")); }
    finally { setDoctorNoteSaving(false); }
  };

  const load = async () => {
    const { data } = await api.get(`/visits/${vid}`);
    setV(data);
    setSedeId(data.sede_id || "");
    const ana = {
      patient_name: data.patient_name || "",
      patient_surname: data.patient_surname || "",
      patient_birth: data.patient_birth || "",
      patient_fiscal_code: data.patient_fiscal_code || "",
      patient_email: data.patient_email || "",
      patient_phone: data.patient_phone || "",
      patient_address: data.patient_address || "",
      patient_city: data.patient_city || "",
      patient_cap: data.patient_cap || "",
      patient_province: data.patient_province || "",
      privacy_contact_consent: !!data.privacy_contact_consent,
    };
    setAnagrafica(ana);
    setAnamnesi(data.anamnesi || "");
    setOD(data.OD || {}); setOS(data.OS || {});
    setAnamnesiStructured(data.anamnesi_structured && typeof data.anamnesi_structured === "object" ? data.anamnesi_structured : {});
    setEyeExcluded(data.eye_excluded && typeof data.eye_excluded === "object" ? data.eye_excluded : {});
    setDiagnosi(data.diagnosi || ""); setTerapia(data.terapia || ""); setReferto(data.referto || "");
    setTerapiaItems(Array.isArray(data.terapia_items) ? data.terapia_items : []);
    setProssimoControllo(data.prossimo_controllo || "");
    setTags(data.tags || []); setBehavioralProtocol(data.behavioral_protocol || "");
    setClinicalHashtags(Array.isArray(data.clinical_hashtags) ? data.clinical_hashtags : []);
    setDiagnostica(Array.isArray(data.diagnostica) ? data.diagnostica.map((x) => typeof x === "string" ? x : (x?.id || x?.name)).filter(Boolean) : []);
    setPhase(data.phase || "previsita");
    setPrivacyConsent(!!data.privacy_consent);
    setSpecialized(data.specialized && typeof data.specialized === "object" ? data.specialized : {});
    setPrescrizioneOcchiali(data.prescrizione_occhiali && typeof data.prescrizione_occhiali === "object" ? data.prescrizione_occhiali : {});
    setConsensoUnico(!!(data.consenso_unico ?? data.privacy_consent));
    setConsensoAcquiredAt(data.consenso_unico_at || null);
    setAnamnesiSource(data.anamnesi_source || null);
    // Fetch anamnesi master + consenso unico dal paziente (per stesso doctor)
    try {
      const params = {
        fiscal_code: data.patient_fiscal_code || undefined,
        name: data.patient_name || undefined,
        surname: data.patient_surname || undefined,
        birth: data.patient_birth || undefined,
      };
      const [{ data: am }, { data: cc }] = await Promise.all([
        api.get("/medico/patient-anamnesi", { params }),
        api.get("/medico/patient-consent", { params }),
      ]);
      setAnamnesiMaster(am || { anamnesi: "", updated_at: null, source: null });
      if (cc?.consenso_unico) {
        setConsensoUnico(true);
        setConsensoAcquiredAt(cc.acquired_at);
      }
    } catch (e) { /* silent fallback */ }

    // Patient tags persistenti (collezione medico_patient_tags)
    try {
      const tagsParams = {};
      if (data.patient_fiscal_code) tagsParams.fiscal_code = data.patient_fiscal_code;
      else { tagsParams.name = data.patient_name; tagsParams.surname = data.patient_surname; if (data.patient_birth) tagsParams.birth = data.patient_birth; }
      const [{ data: pt }, { data: pts }] = await Promise.all([
        api.get("/medico/patient-tags", { params: tagsParams }),
        api.get("/medico/patient-tags/suggestions"),
      ]);
      setPatientTags(pt?.tags || []);
      setPatientTagsUpdatedAt(pt?.updated_at || null);
      setPatientTagSuggestions(pts?.items || []);
    } catch { setPatientTags([]); setPatientTagSuggestions([]); }

    // load nota personale del medico (separata dalla visita)
    loadDoctorNote(ana);

    // load patient history
    try {
      const params = { exclude_id: data.id };
      if (data.patient_fiscal_code) params.fiscal_code = data.patient_fiscal_code;
      else { params.name = data.patient_name; params.surname = data.patient_surname; if (data.patient_birth) params.birth = data.patient_birth; }
      const r = await api.get("/visits/patient-history", { params });
      setHistory(r.data || []);
    } catch { setHistory([]); }
    // editable status (lock 60 min)
    await refreshEditStatus(data.id);
    // Carica template ambulatorio del medico per filtrare blocchi
    try {
      const svc = data.service || "generale";
      const tplKey = SERVICE_TEMPLATE_KEY_MAP[svc] || svc;
      const tplResp = await api.get(`/medico/clinic-templates/${tplKey}`);
      setTemplateBlocks(tplResp.data.blocks || null);
      setTemplateDiag(tplResp.data.diagnostica_default || []);
      // Phase 8: NON auto-applichiamo più le diagnostiche del modello.
      // Restano come "preferiti/favorites" nel DiagnosticaPicker — il medico le aggiunge
      // solo quando vuole, mantenendo la cartella pulita di default.
      // Carica referti diagnostica salvati
      if (data.diagnostica_referti && typeof data.diagnostica_referti === "object") {
        setDiagnosticaReferti(data.diagnostica_referti);
      }
      if (data.diagnostica_allegati && typeof data.diagnostica_allegati === "object") {
        setDiagnosticaAllegati(data.diagnostica_allegati);
      }
    } catch {
      setTemplateBlocks(null);
    }
    // Auto-caricamento default "sano" del modello: SOLO bozze nuove con esame occhi
    // completamente vuoto (mai sovrascrive dati inseriti; visus sempre escluso).
    try {
      const eyesEmpty = (eye) => !Object.values(eye || {}).some((val) => (typeof val === "string" ? val.trim() : val));
      if (data.status !== "closed" && !autoDefaultsAppliedRef.current[data.id] && eyesEmpty(data.OD) && eyesEmpty(data.OS)) {
        autoDefaultsAppliedRef.current[data.id] = true;
        const { data: def } = await api.get(`/medico/settings/tutto-sano`, { params: { service: data.service || "generale" } });
        const mergedOD = mergeEmptyEyeFields(data.OD || {}, def.OD);
        const mergedOS = mergeEmptyEyeFields(data.OS || {}, def.OS);
        if (Object.keys(mergedOD).length || Object.keys(mergedOS).length) {
          const patch = { OD: mergedOD, OS: mergedOS };
          if (def.terapia_base && !(data.terapia || "").trim()) { patch.terapia = def.terapia_base; setTerapia(def.terapia_base); }
          const { data: saved } = await api.patch(`/visits/${data.id}`, patch);
          setOD(mergedOD); setOS(mergedOS);
          if (saved?.updated_at) setV((p) => (p ? { ...p, updated_at: saved.updated_at } : p));
          lastLocalSaveRef.current = Date.now();
          toast.info("Valori base del modello applicati ai campi vuoti (visus escluso)", { duration: 5000 });
        }
      }
    } catch { /* silent: nessun default disponibile */ }
    // Marca caricamento iniziale completo; pulisce dirty (i setState sopra triggerano un re-render dopo)
    setTimeout(() => { initialLoadedRef.current = true; setDirty(false); }, 50);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [vid]);

  // Sedi del medico (per selettore in visita) + config messaggistica (gating SMS)
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/medico/sites"); setSites((data || []).filter((s) => s.active !== false)); }
      catch { setSites([]); }
      try { const { data } = await api.get("/medico/messaging-config"); setMsgConfig(data); }
      catch { setMsgConfig(null); }
    })();
  }, []);

  // Listener: "invariato" / "*" ha ricaricato i campi clinici sul backend → reload locale + reset dirty
  useEffect(() => {
    const handler = async (ev) => {
      if (!vid || ev?.detail?.vid !== vid) return;
      await load();
      // Dopo aver letto i dati appena copiati dal backend, forziamo "non-dirty" per evitare
      // che l'autosave a 5s sovrascriva con stato precedente.
      setTimeout(() => setDirty(false), 100);
    };
    window.addEventListener("pv-visit-reload-clinical", handler);
    return () => window.removeEventListener("pv-visit-reload-clinical", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vid]);

  // Carica i template "tutto sano" del medico per la tipologia visita corrente
  useEffect(() => {
    let cancelled = false;
    const svc = v?.service || "generale";
    (async () => {
      try {
        const { data } = await api.get(`/medico/visit-defaults/${encodeURIComponent(svc)}`);
        if (!cancelled) setHealthyDefaults(data?.fields || null);
      } catch {
        if (!cancelled) setHealthyDefaults(null);
      }
    })();
    return () => { cancelled = true; };
  }, [v?.service]);

  // FASE 2: carica parametri tono corretto + persiste la stima nei campi occhio (mai sovrascrive l'IOP misurata)
  useEffect(() => {
    api.get("/medico/iop-correction").then(({ data }) => setIopCfg(data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!v || v.status === "closed") return;
    const upd = (eye, setEye) => {
      const t = parseFloat(String(eye.tono || "").replace(",", "."));
      const c = parseFloat(String(eye.pachimetria || "").replace(",", "."));
      let next = "", formula = "";
      if (iopCfg?.configured && !isNaN(t) && !isNaN(c)) {
        next = (Math.round((t + ((iopCfg.reference_cct - c) / 10) * iopCfg.coeff_per_10um) * 10) / 10).toFixed(1);
        formula = `${iopCfg.source === "system_default" ? "default sistema v0" : `v${iopCfg.version}`} · CCT rif ${iopCfg.reference_cct} µm · ${iopCfg.coeff_per_10um} mmHg/10µm`;
      }
      if ((eye.tono_corretto || "") !== next || (eye.tono_corretto_formula || "") !== formula) {
        setEye((p) => ({ ...p, tono_corretto: next, tono_corretto_formula: formula }));
      }
    };
    upd(OD, setOD); upd(OS, setOS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [OD.tono, OD.pachimetria, OS.tono, OS.pachimetria, iopCfg, v?.status]);

  if (!v) return <AppShell title="Visita"><div className="text-slate-500">Caricamento…</div></AppShell>;

  const template = templateForService(v.service);
  const eyeFields = buildEyeFields(template);
  // Visita è readOnly se chiusa MA fuori finestra modifica (60min); in finestra: solo bottoni report sono editabili (editReportMode)
  const isClosed = v.status === "closed";
  const inEditWindow = !!editStatus?.editable && editStatus?.reason !== "draft";
  const readOnly = isClosed && !(inEditWindow && editReportMode);

  // Tutto sano: chiama backend e fonde nei campi
  const applyTuttoSano = async (overwrite = false) => {
    try {
      const { data } = await api.get(`/medico/settings/tutto-sano`, { params: { service: v.service } });
      // NEVER autofill visus naturale / corretto: deve essere SEMPRE inserito dal medico
      const VISUS_PROTECTED = new Set([
        "visus_naturale", "visus_corretto", "visus_naturale_od", "visus_naturale_os",
        "visus_corretto_od", "visus_corretto_os",
      ]);
      const merge = (cur, def) => {
        const out = { ...cur };
        Object.entries(def || {}).forEach(([k, val]) => {
          if (VISUS_PROTECTED.has(k)) return; // mai compilato in automatico
          if (val && (overwrite || !cur?.[k])) out[k] = val;
        });
        return out;
      };
      setOD((p) => merge(p, data.OD));
      setOS((p) => merge(p, data.OS));
      // Terapia base del modello (Referti base): compilata solo se vuota
      if (data.terapia_base && (overwrite || !terapia)) setTerapia(data.terapia_base);
      toast.success(overwrite ? "Tutto sano applicato (sovrascrittura completa)" : "Tutto sano applicato (solo campi vuoti, visus escluso)");
    } catch (e) { toast.error(apiErr(e, "Errore caricamento default")); }
  };

  // Cambio modello cartella su bozza attiva: conferma inline, mai sovrascrive campi compilati
  const changeService = async (newSvc) => {
    if (!newSvc || newSvc === (v.service || "generale")) return;
    const label = serviceLabel(newSvc);
    try {
      const { data: saved } = await api.patch(`/visits/${vid}`, { service: newSvc });
      lastLocalSaveRef.current = Date.now();
      setV((p) => (p ? { ...p, service: newSvc, updated_at: saved?.updated_at || p.updated_at } : p));
      const tplKey = SERVICE_TEMPLATE_KEY_MAP[newSvc] || newSvc;
      try {
        const tplResp = await api.get(`/medico/clinic-templates/${tplKey}`);
        setTemplateBlocks(tplResp.data.blocks || null);
        setTemplateDiag(tplResp.data.diagnostica_default || []);
      } catch { setTemplateBlocks(null); }
      try {
        const { data: def } = await api.get(`/medico/settings/tutto-sano`, { params: { service: newSvc } });
        setOD((p) => mergeEmptyEyeFields(p, def.OD));
        setOS((p) => mergeEmptyEyeFields(p, def.OS));
        setDirty(true);
      } catch { /* silent */ }
      toast.success(`Modello cartella cambiato in "${label}" — default applicati solo ai campi vuoti`);
    } catch (e) { toast.error(apiErr(e, "Errore cambio modello")); }
  };

  // Visibility helper: se non c'è template, mostra tutto (back-compat). Se c'è, mostra solo blocchi attivi.
  // Fase 31: l'anamnesi è dato master del paziente e deve essere SEMPRE visibile,
  // indipendentemente dal modello visita selezionato.
  const showBlock = (key) => {
    if (key === "anamnesi") return true;
    if (!templateBlocks || templateBlocks.length === 0) return true;
    return templateBlocks.includes(key);
  };

  // PDF + Stampa (Fase 42: fix popup bloccata).
  // Strategia: apri SUBITO al click una finestra placeholder (dentro il gesto utente),
  // poi carica il blob e imposta location. Se popup blocked, fallback a download <a>.
  const openPdf = async (forPrint = false) => {
    // Apri la finestra IMMEDIATAMENTE nel gesto click (prima di ogni async)
    // per non incappare nel blocco popup di Safari/Chrome.
    let printWin = null;
    if (forPrint) {
      // NB: niente "noopener,noreferrer" qui — su Chromium window.open con noopener ritorna null
      try { printWin = window.open("", "_blank"); } catch { printWin = null; }
    }
    try {
      const { data } = await api.get(`/visits/${vid}/pdf`, { responseType: "blob" });
      const blob = new Blob([data], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);

      if (forPrint) {
        if (printWin && !printWin.closed) {
          // popup consentito: carica il PDF e stampa
          try {
            printWin.location.href = blobUrl;
            printWin.focus();
            // print automatico dopo un breve delay (necessario perché il PDF renderer del browser deve completare)
            setTimeout(() => { try { printWin.print(); } catch { /* silent */ } }, 800);
          } catch { /* silent */ }
        } else {
          // popup BLOCCATA → fallback download come <a>
          const a = document.createElement("a");
          a.href = blobUrl;
          a.download = `visita_${vid}.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          toast.info("Il browser ha bloccato la finestra di stampa. Il PDF è stato scaricato: aprilo e usa la stampa del visualizzatore.", { duration: 8000 });
        }
      } else {
        // Solo apertura in nuova scheda (link diretto)
        const a = document.createElement("a");
        a.href = blobUrl;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      // Cleanup blob URL dopo qualche minuto
      setTimeout(() => URL.revokeObjectURL(blobUrl), 120000);
      // Audit
      await api.post(`/visits/${vid}/send-report`, { channel: forPrint ? "print" : "download" }).catch(() => {});
      if (!forPrint) toast.success("PDF aperto/scaricato");
    } catch (e) {
      if (printWin && !printWin.closed) { try { printWin.close(); } catch { /* silent */ } }
      toast.error(apiErr(e, "Errore PDF"));
    }
  };

  // Autosave bozza: scrive solo i campi cartella, silenzioso (no toast), con indicator
  const autosaveDraft = async () => {
    if (!vid || !v || v.status === "closed") return;
    setAutosaveStatus("saving");
    try {
      const payload = {
        ...anagrafica,
        sede_id: sedeId || null,
        phase, anamnesi, OD, OS, diagnosi, terapia, referto, tags,
        clinical_hashtags: clinicalHashtags,
        behavioral_protocol: behavioralProtocol,
        diagnostica: diagnostica.map((id) => ({ id, label: (DIAGNOSTICA_OPTIONS.find((o) => o.id === id) || {}).label || id })),
        diagnostica_referti: diagnosticaReferti,
        diagnostica_allegati: diagnosticaAllegati,
        specialized,
        prescrizione_occhiali: prescrizioneOcchiali,
        consenso_unico: consensoUnico,
        privacy_consent: consensoUnico || privacyConsent,
        terapia_items: terapiaItems,
        anamnesi_structured: anamnesiStructured,
        eye_excluded: eyeExcluded,
      };
      const { data: saved } = await api.patch(`/visits/${vid}`, payload);
      // CRITICO anti self-echo: aggiorna subito updated_at locale, altrimenti il polling
      // live vede il PROPRIO autosave come modifica remota e sovrascrive lo stato
      // (perdita caratteri durante la digitazione — bug iteration_10).
      if (saved?.updated_at) setV((p) => (p ? { ...p, updated_at: saved.updated_at } : p));
      lastLocalSaveRef.current = Date.now();
      setAutosaveStatus("saved");
      setAutosaveAt(new Date().toISOString());
      setDirty(false);
    } catch (e) {
      setAutosaveStatus("error");
    }
  };
  autosaveDraftRef.current = autosaveDraft;

  // Debounced autosave su ogni dirty change (5s) → spostato sopra l'early return per rules-of-hooks (vedi sopra)

  // Navigazione sicura: se dirty, apri modal
  const safeNav = (action) => {
    if (dirty) {
      setPendingExitAction(() => action);
      setExitConfirmOpen(true);
    } else {
      action();
    }
  };

  const handleExitSaveDraft = async () => {
    await autosaveDraft();
    setExitConfirmOpen(false);
    if (pendingExitAction) pendingExitAction();
    else nav("/visite");
  };
  const handleExitDiscard = () => {
    setDirty(false);
    setExitConfirmOpen(false);
    if (pendingExitAction) pendingExitAction();
    else nav("/visite");
  };
  const handleExitCancel = () => {
    setExitConfirmOpen(false);
    setPendingExitAction(null);
  };

  // Salvataggio referto nella finestra 60min
  const saveReportEdit = async () => {
    setSavingReportEdit(true);
    try {
      const { data } = await api.patch(`/visits/${vid}/report`, {
        referto, diagnosi, terapia, behavioral_protocol: behavioralProtocol,
        prossimo_controllo: prossimoControllo,
        reason: editReason || null,
      });
      toast.success("Referto aggiornato");
      setEditReportMode(false);
      setEditReason("");
      if (data.minutes_remaining !== undefined) setEditStatus(data);
      load();
    } catch (e) { toast.error(apiErr(e, "Errore aggiornamento referto")); }
    finally { setSavingReportEdit(false); }
  };

  const isEyeExcluded = (side) => !!(eyeExcluded[side] || {}).excluded;
  const toggleEyeExclusion = (side) => {
    const cur = eyeExcluded[side] || {};
    if (cur.excluded) {
      setEyeExcluded((p) => ({ ...p, [side]: { ...cur, excluded: false } }));
      return;
    }
    // prompt FUORI dall'updater (StrictMode: gli updater possono girare due volte)
    const reason = window.prompt(`Motivo esclusione ${side} (occhio spento, protesi, bendato, non valutabile…)`, cur.reason || "") || "";
    setEyeExcluded((p) => ({ ...p, [side]: { excluded: true, reason } }));
  };

  const copyTo = (from) => {
    const to = from === "OD" ? "OS" : "OD";
    if (isEyeExcluded(to)) {
      if (!window.confirm(`${to} è escluso (${(eyeExcluded[to] || {}).reason || "motivo non indicato"}). Copiare comunque?`)) return;
    }
    if (from === "OD") setOS({ ...OD }); else setOD({ ...OS });
    toast.success(`OU: copiato da ${from}`);
  };
  const RELATED_COPY_KEYS = {
    autoref_no_ciclo: ["autoref_no_ciclo", "autoref_no_ciclo_sph", "autoref_no_ciclo_cyl", "autoref_no_ciclo_axis"],
    autoref_ciclo: ["autoref_ciclo", "autoref_ciclo_sph", "autoref_ciclo_cyl", "autoref_ciclo_axis"],
    tono: ["tono", "tono_metodo"],
    visus_naturale: ["visus_naturale", "visus_naturale_vicino"],
    visus_corretto: ["visus_corretto", "visus_corretto_vicino"],
    posizione_refrazione: ["posizione_refrazione", "posizione_refrazione_altro"],
  };
  const copyField = (from, key) => {
    const to = from === "OD" ? "OS" : "OD";
    if (isEyeExcluded(to)) { toast.error(`${to} è escluso: sblocca l'occhio prima di copiare`); return; }
    const keys = RELATED_COPY_KEYS[key] || [key];
    const src = from === "OD" ? OD : OS;
    const patch = Object.fromEntries(keys.map((k) => [k, src[k]]));
    if (from === "OD") setOS((p) => ({ ...p, ...patch })); else setOD((p) => ({ ...p, ...patch }));
  };

  const save = async () => {
    setSaving(true);
    try {
      // Se la visita è chiusa ma in modalità edit del referto (finestra 60min), usa l'endpoint dedicato
      if (isClosed && editReportMode) {
        await saveReportEdit();
        return;
      }
      const payload = {
        ...anagrafica,
        sede_id: sedeId || null,
        phase, anamnesi, OD, OS, diagnosi, terapia, referto, tags,
        clinical_hashtags: clinicalHashtags,
        behavioral_protocol: behavioralProtocol,
        diagnostica: diagnostica.map((id) => ({ id, label: (DIAGNOSTICA_OPTIONS.find((o) => o.id === id) || {}).label || id })),
        diagnostica_referti: diagnosticaReferti,
        diagnostica_allegati: diagnosticaAllegati,
        specialized,
        prescrizione_occhiali: prescrizioneOcchiali,
        consenso_unico: consensoUnico,
        privacy_consent: consensoUnico || privacyConsent,
        terapia_items: terapiaItems,
        prossimo_controllo: prossimoControllo,
        anamnesi_structured: anamnesiStructured,
        eye_excluded: eyeExcluded,
      };
      await api.patch(`/visits/${vid}`, payload);
      lastLocalSaveRef.current = Date.now();
      toast.success("Salvato");
      setDirty(false);
      setAutosaveStatus("saved");
      setAutosaveAt(new Date().toISOString());
      load();
    } catch (e) { toast.error(apiErr(e, "Errore")); }
    finally { setSaving(false); }
  };

  const close = async () => {
    if (!window.confirm("Chiudere la visita e generare referto? Dopo non sarà più modificabile (solo addendum).")) return;
    setClosing(true);
    try {
      await save();
      await api.post(`/visits/${vid}/close`);
      toast.success("Visita chiusa e referto disponibile");
      load();
    } catch (e) { toast.error(apiErr(e, "Errore")); }
    finally { setClosing(false); }
  };

  const sendReport = async (channel) => {
    try {
      if (channel === "print") {
        await openPdf(true);
        setSendOpen(false);
        return;
      }
      const { data } = await api.post(`/visits/${vid}/send-report`, { channel });
      // Esito dettagliato per canale
      const ok = (data?.delivery || []).filter((d) => d.ok && d.channel !== "print");
      const fail = (data?.delivery || []).filter((d) => !d.ok && d.channel !== "print");
      ok.forEach((d) => {
        if (d.channel === "email") toast.success(`Email inviata con PDF allegato (${(d.attachment_bytes/1024).toFixed(1)} KB) · msgId ${d.messageId || "—"}`);
        else if (d.channel === "sms") toast.success(`SMS inviato a ${d.normalized || d.to} · msgId ${d.messageId || "—"}`);
        else if (d.channel === "whatsapp") toast.success(`WhatsApp inviato a ${d.to} · msgId ${d.messageId || "—"}`);
      });
      fail.forEach((d) => toast.error(`${d.channel.toUpperCase()} fallito: ${d.error || "errore Brevo"}`));
      if (ok.length) setSendOpen(false);
      load();
    } catch (e) { toast.error(apiErr(e, "Errore invio referto")); }
  };

  const addAddendum = async () => {
    if (!addendumText.trim()) return;
    try {
      await api.post(`/visits/${vid}/addendum`, { text: addendumText });
      setAddendumText("");
      toast.success("Addendum aggiunto");
      load();
    } catch { toast.error("Errore"); }
  };

  const addDrug = (item) => {
    setTerapiaItems((p) => [...p, item]);
    // Append automaticamente al campo Terapia (testo libero) per integrazione visiva
    const drugName = item?.drug_name || item?.name || item?.label || "";
    const posology = item?.posology || item?.posology_hint || "";
    const ap = item?.active_principle ? ` (${item.active_principle})` : "";
    const line = `${drugName}${ap}${posology ? " — " + posology : ""}`.trim();
    if (line) {
      setTerapia((prev) => prev ? `${prev}\n${line}` : line);
    }
  };
  const removeDrug = (idx) => setTerapiaItems((p) => p.filter((_, i) => i !== idx));

  const acceptClinical = ({ kind, text, tags: applyTags }) => {
    if (kind === "diagnosi" && text) { setDiagnosi((p) => p ? `${p}\n— IA: ${text}` : text); toast.success("Diagnosi IA applicata"); }
    else if (kind === "terapia" && text) { setTerapia((p) => p ? `${p}\n${text}` : text); toast.success("Terapia IA applicata (testo libero)"); }
    else if (kind === "protocollo" && text) { setBehavioralProtocol((p) => p ? `${p}\n${text}` : text); toast.success("Protocollo IA applicato"); }
    else if (kind === "tags" && Array.isArray(applyTags)) { setTags((p) => Array.from(new Set([...(p || []), ...applyTags]))); toast.success(`${applyTags.length} tag aggiunti`); }
  };

  // Dettatura IA: applica solo i campi selezionati nell'anteprima
  const applyDictation = (payload) => {
    if (!payload) return;
    // Snapshot per undo "Annulla ultimo inserimento IA"
    setAiSnapshot({
      OD: { ...OD }, OS: { ...OS }, anamnesi, anamnesiStructured: JSON.parse(JSON.stringify(anamnesiStructured || {})),
      diagnosi, terapia, behavioralProtocol, prossimoControllo, at: Date.now(),
    });
    // Comandi vocali dal transcript (deterministici)
    const tr = String(payload.transcript || "").toLowerCase();
    if (/escludi (l')?occhio destro|escludi od\b/.test(tr) && !isEyeExcluded("OD")) {
      setEyeExcluded((p) => ({ ...p, OD: { excluded: true, reason: "comando vocale" } }));
      toast.info("Comando vocale: OD escluso");
    }
    if (/escludi (l')?occhio sinistro|escludi os\b/.test(tr) && !isEyeExcluded("OS")) {
      setEyeExcluded((p) => ({ ...p, OS: { excluded: true, reason: "comando vocale" } }));
      toast.info("Comando vocale: OS escluso");
    }
    if (/copia (l')?(occhio )?destro (in|su) (occhio )?sinistro|copia od in os/.test(tr)) {
      if (!isEyeExcluded("OS")) { setOS({ ...OD }); toast.info("Comando vocale: copiato OD → OS"); }
    } else if (/copia (l')?(occhio )?sinistro (in|su) (occhio )?destro|copia os in od/.test(tr)) {
      if (!isEyeExcluded("OD")) { setOD({ ...OS }); toast.info("Comando vocale: copiato OS → OD"); }
    }
    if (payload.anamnesi) {
      setAnamnesi(payload.anamnesi);
      // Autosbarra anamnesi strutturata dal testo dettato (deterministico, mai inventa)
      setAnamnesiStructured((prev) => deriveAnamnesiStructured(payload.anamnesi, prev));
    }
    // Fase 31B: se il backend propone un aggiornamento dell'anamnesi master (senza sovrascriverla),
    // apri il banner giallo di conferma. Non applicare automaticamente.
    if (typeof payload.anamnesi_suggested_update === "string" && payload.anamnesi_suggested_update.trim()) {
      setAnamnesiSuggested(payload.anamnesi_suggested_update.trim());
    }
    if (payload.OD && Object.keys(payload.OD).length && !isEyeExcluded("OD")) setOD((prev) => ({ ...prev, ...payload.OD }));
    if (payload.OS && Object.keys(payload.OS).length && !isEyeExcluded("OS")) setOS((prev) => ({ ...prev, ...payload.OS }));
    if ((payload.OD && isEyeExcluded("OD")) || (payload.OS && isEyeExcluded("OS"))) {
      toast.warning("Occhio escluso: i campi IA per l'occhio escluso NON sono stati applicati");
    }
    if (Array.isArray(payload.tags) && payload.tags.length) {
      setTags((prev) => Array.from(new Set([...(prev || []), ...payload.tags])));
    }
    // Hashtag clinici dalla pipeline IA: merge senza duplicati, modificabili dal medico
    if (Array.isArray(payload.hashtags)) {
      const norm = payload.hashtags
        .map((h) => String(h || "").trim().toLowerCase())
        .map((h) => (h && !h.startsWith("#") ? `#${h}` : h))
        .filter(Boolean);
      if (norm.length) {
        setClinicalHashtags((prev) => Array.from(new Set([...(prev || []), ...norm])));
      }
    }
    // Diagnosi/terapia/protocollo dalla pipeline unica IA — NON sovrascrive valori già scritti dal medico
    if (Array.isArray(payload.diagnosi_suggerite) && payload.diagnosi_suggerite.length) {
      const ds = payload.diagnosi_suggerite
        .filter((d) => d && d.diagnosi)
        .map((d) => {
          const pct = (d.compatibilita ?? null);
          const pctTxt = pct !== null && pct !== undefined ? ` (${pct}% compatibilità)` : "";
          const motiv = d.motivazione ? ` — ${d.motivazione}` : "";
          return `${d.diagnosi}${pctTxt}${motiv}`;
        })
        .join("\n");
      if (ds) setDiagnosi((prev) => (prev && prev.trim() ? prev : ds));
    }
    if (payload.terapia_suggerita) {
      setTerapia((prev) => (prev && prev.trim() ? prev : payload.terapia_suggerita));
    }
    if (payload.protocollo_suggerito) {
      setBehavioralProtocol((prev) => (prev && prev.trim() ? prev : payload.protocollo_suggerito));
    }
    if (payload.prossimo_controllo_consigliato) {
      setProssimoControllo((prev) => (prev && prev.trim() ? prev : payload.prossimo_controllo_consigliato));
    }
  };

  const undoAiApply = () => {
    if (!aiSnapshot) return;
    setOD(aiSnapshot.OD); setOS(aiSnapshot.OS);
    setAnamnesi(aiSnapshot.anamnesi); setAnamnesiStructured(aiSnapshot.anamnesiStructured);
    setDiagnosi(aiSnapshot.diagnosi); setTerapia(aiSnapshot.terapia);
    setBehavioralProtocol(aiSnapshot.behavioralProtocol); setProssimoControllo(aiSnapshot.prossimoControllo);
    setAiSnapshot(null);
    toast.success("Ultimo inserimento IA annullato");
  };

  // Applica un template rapido esame obiettivo agli occhi indicati (rispetta esclusioni)
  const applyExamPreset = (fields, targets) => {
    const patch = Object.fromEntries(Object.entries(fields || {}).filter(([, v]) => (v || "").trim()));
    if (targets.includes("OD")) setOD((p) => ({ ...p, ...patch }));
    if (targets.includes("OS")) setOS((p) => ({ ...p, ...patch }));
  };

  // === TAG PAZIENTE PERSISTENTI ===
  const normalizeTag = (raw) => {
    let t = (raw || "").trim().toLowerCase().replace(/\s+/g, "-");
    if (!t) return "";
    if (!t.startsWith("#")) t = "#" + t;
    return t.slice(0, 40);
  };

  const savePatientTags = async (next) => {
    setPatientTagsSaving(true);
    try {
      const params = {};
      if (anagrafica.patient_fiscal_code) params.fiscal_code = anagrafica.patient_fiscal_code;
      else { params.name = anagrafica.patient_name; params.surname = anagrafica.patient_surname; if (anagrafica.patient_birth) params.birth = anagrafica.patient_birth; }
      const { data } = await api.put("/medico/patient-tags", { ...params, tags: next });
      setPatientTags(data.tags || []);
      setPatientTagsUpdatedAt(data.updated_at);
    } catch (e) { toast.error(apiErr(e, "Errore salvataggio tag paziente")); }
    finally { setPatientTagsSaving(false); }
  };

  const addPatientTag = (raw) => {
    const t = normalizeTag(raw);
    if (!t) return;
    if (patientTags.includes(t)) { setPatientTagInput(""); return; }
    const next = [...patientTags, t];
    setPatientTags(next);
    setPatientTagInput("");
    savePatientTags(next);
  };

  const removePatientTag = (t) => {
    const next = patientTags.filter((x) => x !== t);
    setPatientTags(next);
    savePatientTags(next);
  };

  const applyAiTagSuggestions = () => {
    if (!Array.isArray(tags) || tags.length === 0) { toast.info("Nessun tag IA da applicare"); return; }
    const next = Array.from(new Set([...patientTags, ...tags.map(normalizeTag).filter(Boolean)]));
    if (next.length === patientTags.length) { toast.info("Tag già presenti"); return; }
    setPatientTags(next);
    savePatientTags(next);
    toast.success("Tag IA applicati al profilo paziente");
  };

  // Suggerimenti IA: tag proposti dalla pipeline che il medico può confermare (mai auto-applicati)
  const aiTagSuggestions = Array.from(new Set((tags || []).map(normalizeTag).filter((t) => t && !patientTags.includes(t))));

  return (
    <AppShell
      title={`Visita · ${v.patient_full_name}`}
      subtitle={`${serviceLabel(v.service || "generale")} · ${new Date(v.created_at).toLocaleDateString("it-IT")} ${v.status === "closed" ? "· chiusa" : "· bozza"}`}
      rightSlot={
        <div className="flex items-center gap-2">
          {v.status === "closed" && <span className="px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold flex items-center gap-1.5"><Lock size={12} /> Chiusa</span>}
        </div>
      }
    >
      {/* Layout: colonna principale + colonna destra (storico/appuntamenti/salute occhi) */}
      <div className="flex gap-5 items-start">
      <div className="flex-1 min-w-0">
      {/* Flusso unico della visita: fasi Previsita/Oculista unificate (switcher rimosso).
          Lo stato `phase` resta salvato per retrocompatibilità ma non gating UI. */}

      {/* Anagrafica paziente */}
      <div className="bg-white rounded-xl border border-[#E2E8F0] p-5 mb-4" data-testid="anagrafica-block">
        <div className="flex items-center gap-2 mb-3"><UserCircle className="text-brand-azure" size={18} /><h3 className="font-extrabold text-brand-navy">Anagrafica paziente</h3></div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs font-bold text-slate-500">Nome</label>
            <input data-testid="ana-name" disabled={readOnly} value={anagrafica.patient_name} onChange={(e) => setAnagrafica({ ...anagrafica, patient_name: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0] disabled:bg-slate-50 text-sm" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500">Cognome</label>
            <input data-testid="ana-surname" disabled={readOnly} value={anagrafica.patient_surname} onChange={(e) => setAnagrafica({ ...anagrafica, patient_surname: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0] disabled:bg-slate-50 text-sm" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500">Data nascita</label>
            <input type="date" data-testid="ana-birth" disabled={readOnly} value={anagrafica.patient_birth} onChange={(e) => setAnagrafica({ ...anagrafica, patient_birth: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0] disabled:bg-slate-50 text-sm" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500">Codice fiscale</label>
            <input data-testid="ana-cf" disabled={readOnly} value={anagrafica.patient_fiscal_code} onChange={(e) => setAnagrafica({ ...anagrafica, patient_fiscal_code: e.target.value.toUpperCase() })} maxLength={16} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0] uppercase font-mono text-sm disabled:bg-slate-50" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500">Telefono</label>
            <input data-testid="ana-phone" disabled={readOnly} value={anagrafica.patient_phone} onChange={(e) => setAnagrafica({ ...anagrafica, patient_phone: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0] disabled:bg-slate-50 text-sm" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500">Email</label>
            <input data-testid="ana-email" disabled={readOnly} value={anagrafica.patient_email} onChange={(e) => setAnagrafica({ ...anagrafica, patient_email: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0] disabled:bg-slate-50 text-sm" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-bold text-slate-500">Indirizzo</label>
            <input data-testid="ana-address" disabled={readOnly} value={anagrafica.patient_address} onChange={(e) => setAnagrafica({ ...anagrafica, patient_address: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0] disabled:bg-slate-50 text-sm" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500">Città</label>
            <input data-testid="ana-city" disabled={readOnly} value={anagrafica.patient_city} onChange={(e) => setAnagrafica({ ...anagrafica, patient_city: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0] disabled:bg-slate-50 text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-bold text-slate-500">CAP</label>
              <input data-testid="ana-cap" disabled={readOnly} value={anagrafica.patient_cap} onChange={(e) => setAnagrafica({ ...anagrafica, patient_cap: e.target.value })} maxLength={5} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0] disabled:bg-slate-50 text-sm" />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500">Prov.</label>
              <input data-testid="ana-province" disabled={readOnly} value={anagrafica.patient_province} onChange={(e) => setAnagrafica({ ...anagrafica, patient_province: e.target.value.toUpperCase() })} maxLength={2} className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E2E8F0] uppercase disabled:bg-slate-50 text-sm" />
            </div>
          </div>
        </div>
      </div>

      {/* Phase 27: Consenso privacy paziente 3-punti (in-studio / link SMS) */}
      {(anagrafica.patient_name && anagrafica.patient_surname) && (
        <div className="mb-4">
          <PatientConsentBlock
            patient={{
              fiscal_code: anagrafica.patient_fiscal_code,
              name: anagrafica.patient_name,
              surname: anagrafica.patient_surname,
              birth: anagrafica.patient_birth,
              phone: anagrafica.patient_phone,
              email: anagrafica.patient_email,
            }}
            legacy={{ acquired: !!consensoUnico, acquiredAt: consensoAcquiredAt }}
          />
        </div>
      )}

      {/* Selettore Sede / Studio + Tag persistenti paziente */}
      {/* Live multi-postazione: presence + conflict */}
      {(activeSessions.length > 0 || conflict) && (
        <div className="mb-3 space-y-2" data-testid="live-presence-area">
          {activeSessions.length > 0 && (
            <div className="bg-brand-azure/5 border border-brand-azure/30 rounded-lg px-3 py-2 flex flex-wrap items-center gap-2 text-xs" data-testid="active-sessions-banner">
              <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span></span>
              <span className="font-bold text-brand-navy">Altre postazioni attive ({activeSessions.length}):</span>
              {activeSessions.map((s) => (
                <span key={s.session_id} className="bg-white border border-[#E2E8F0] rounded-full px-2 py-0.5 font-mono text-[10px]" data-testid={`active-session-${s.session_id}`}>
                  {s.workstation || s.user_name} · {new Date(s.last_ping_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                </span>
              ))}
              {lastSync && <span className="text-slate-400 text-[10px] ml-auto">sync {new Date(lastSync).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>}
            </div>
          )}
          {conflict && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg px-3 py-2 flex flex-wrap items-center justify-between gap-2 text-xs" data-testid="live-conflict-banner">
              <span className="text-amber-900"><AlertTriangle size={12} className="inline mr-1" /> Aggiornato da altra postazione ({conflict.by}). Salva o ricarica per sincronizzare.</span>
              <div className="flex gap-2">
                <button onClick={() => { dismissConflict(); window.location.reload(); }} data-testid="live-conflict-reload" className="bg-amber-600 hover:bg-amber-700 text-white font-bold text-[10px] px-2 py-1 rounded">Ricarica</button>
                <button onClick={dismissConflict} data-testid="live-conflict-dismiss" className="bg-white border border-amber-300 text-amber-700 font-bold text-[10px] px-2 py-1 rounded">Ignora</button>
              </div>
            </div>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {/* SEDE / STUDIO */}
        <div
          className="bg-white rounded-xl border p-5 transition-colors"
          data-testid="visit-sede-block"
          style={(() => {
            const s = sites.find((x) => x.id === sedeId);
            if (!s) return { borderColor: "#E2E8F0" };
            const col = s.color || "#2C4667";
            return { borderColor: `${col}55`, borderLeftWidth: 4, borderLeftColor: col, background: `${col}08` };
          })()}
        >
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <MapPin className="text-brand-azure" size={18} />
            <h3 className="font-extrabold text-brand-navy">Sede / Studio</h3>
            {(() => {
              const s = sites.find((x) => x.id === sedeId);
              if (!s) return null;
              const col = s.color || "#2C4667";
              return (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider"
                  data-testid="visit-sede-selected-badge"
                  style={{ background: col, color: "#fff" }}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-white" />
                  {s.name}
                </span>
              );
            })()}
            <span className="text-[10px] uppercase tracking-wider text-slate-500">la sede compare nel referto PDF</span>
          </div>
          {sites.length === 0 ? (
            <div className="text-xs text-slate-500 italic">
              Nessuna sede configurata. <button onClick={() => nav("/sedi")} className="text-brand-azure font-bold hover:underline" data-testid="visit-sede-go-config">Configura le tue sedi</button>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="visit-sedi-list">
              {sites.map((s) => {
                const selected = sedeId === s.id;
                const col = s.color || "#2C4667";
                // Fase 35: quando selezionata la sede, usa il suo colore come background + border
                const style = selected
                  ? { background: col, borderColor: col, color: "#fff" }
                  : { borderColor: `${col}44` };
                return (
                  <button
                    key={s.id}
                    type="button"
                    disabled={readOnly}
                    data-testid={`visit-sede-${s.id}`}
                    onClick={() => setSedeId(s.id)}
                    style={style}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 text-xs font-bold transition disabled:opacity-50 ${selected ? "" : "bg-white text-slate-700 hover:opacity-80"}`}
                  >
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: selected ? "#fff" : col }}></span>
                    {s.name}
                  </button>
                );
              })}
              <button
                type="button"
                disabled={readOnly}
                data-testid="visit-sede-none"
                onClick={() => setSedeId("")}
                className={`px-3 py-1.5 rounded-full border-2 text-xs font-bold transition disabled:opacity-50 ${!sedeId ? "bg-slate-100 text-slate-700 border-slate-300" : "bg-white text-slate-400 border-[#E2E8F0] hover:border-slate-300"}`}
              >Nessuna</button>
            </div>
          )}
        </div>

        {/* TAG PAZIENTE PERSISTENTI */}
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-5" data-testid="patient-tags-block">
          <div className="flex items-center gap-2 mb-2">
            <Tag className="text-brand-azure" size={18} />
            <h3 className="font-extrabold text-brand-navy">Tag paziente</h3>
            <span className="text-[10px] uppercase tracking-wider text-slate-500">salvati sul paziente, visibili in tutte le sue visite</span>
            {patientTagsSaving && <Loader2 className="animate-spin text-slate-400" size={12} />}
          </div>
          <div className="flex flex-wrap gap-2 mb-2" data-testid="patient-tags-chips">
            {patientTags.length === 0 && <span className="text-xs text-slate-400 italic">Nessun tag</span>}
            {patientTags.map((t) => (
              <span key={t} className="px-2.5 py-1 rounded-full bg-brand-azure text-white text-xs font-bold flex items-center gap-1" data-testid={`patient-tag-${t.replace("#","")}`}>
                {t}
                {!readOnly && <button onClick={() => removePatientTag(t)} className="hover:text-brand-orange"><XIcon size={11} /></button>}
              </span>
            ))}
          </div>
          {!readOnly && (
            <>
              <div className="flex gap-2 mb-2">
                <input
                  data-testid="patient-tag-input"
                  list="patient-tag-suggestions"
                  value={patientTagInput}
                  onChange={(e) => setPatientTagInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addPatientTag(patientTagInput))}
                  placeholder="#cronico, #anziano, #post-cataratta…"
                  className="flex-1 px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm"
                />
                <datalist id="patient-tag-suggestions">
                  {patientTagSuggestions.map((s) => <option key={s.tag} value={s.tag}>{`${s.tag} (${s.count})`}</option>)}
                </datalist>
                <button data-testid="patient-tag-add" onClick={() => addPatientTag(patientTagInput)} className="bg-brand-azure hover:bg-brand-azureDark text-white font-bold px-3 py-2 rounded-lg text-sm">Aggiungi</button>
              </div>
              {aiTagSuggestions.length > 0 && (
                <div data-testid="patient-tags-ai-suggestions">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1 flex items-center gap-1"><Sparkles size={10} /> Suggerimenti IA — clicca un tag per confermarlo</div>
                  <div className="flex flex-wrap gap-1.5 items-center">
                    {aiTagSuggestions.map((t) => (
                      <button key={t} type="button" data-testid={`ai-tag-suggestion-${t.replace("#", "")}`} onClick={() => addPatientTag(t)}
                        className="px-2.5 py-1 rounded-full border-2 border-dashed border-brand-azure/60 text-brand-navy text-xs font-bold hover:bg-brand-azure/10 flex items-center gap-1">
                        <Plus size={10} /> {t}
                      </button>
                    ))}
                    <button type="button" data-testid="patient-tags-apply-ai" onClick={applyAiTagSuggestions} className="text-[11px] font-bold text-white bg-brand-azure hover:bg-brand-azureDark px-2.5 py-1 rounded-full">Applica tutti</button>
                    <button type="button" data-testid="patient-tags-dismiss-ai" onClick={() => setTags([])} className="text-[11px] font-bold text-slate-500 hover:bg-slate-100 px-2 py-1 rounded-full">Ignora</button>
                  </div>
                </div>
              )}
            </>
          )}
          {patientTagsUpdatedAt && <div className="text-[10px] text-slate-400 mt-2">Aggiornati il {new Date(patientTagsUpdatedAt).toLocaleString("it-IT")}</div>}
        </div>
      </div>

      {/* Consenso unico paziente: gestito in un solo blocco (PatientConsentBlock, in alto).
          Il vecchio ConsensiBlock (checkbox duplicata) è stato rimosso; lo stato consenso_unico
          legacy resta salvato per retrocompatibilità ed è mappato nel blocco unico. */}

      {/* Note personali del medico — visibili SOLO al medico, non vanno nel referto */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-4" data-testid="doctor-personal-note-block">
        <div className="flex items-center gap-2 mb-2">
          <StickyNote className="text-amber-700" size={18} />
          <h3 className="font-extrabold text-amber-900">Note personali del medico</h3>
          <span className="text-[10px] uppercase tracking-wider bg-amber-200 text-amber-900 px-2 py-0.5 rounded font-bold">Solo tu</span>
        </div>
        <div className="text-[11px] text-amber-800 mb-2 flex items-start gap-1.5">
          <AlertTriangle size={11} className="mt-0.5" />
          <span>Appunti privati sul paziente. <b>NON</b> compaiono in referto, PDF, email/SMS, link pubblico o accesso admin. Si aggiornano sul profilo del paziente: l&apos;ultima versione resta visibile su tutte le sue visite.</span>
        </div>
        <textarea
          data-testid="doctor-personal-note"
          rows={3}
          disabled={readOnly}
          value={doctorNote}
          onChange={(e) => setDoctorNote(e.target.value)}
          onBlur={(e) => { if (e.target.value !== (doctorNoteMeta._lastSaved || "")) saveDoctorNote(e.target.value); setDoctorNoteMeta((p) => ({ ...p, _lastSaved: e.target.value })); }}
          placeholder="es. Paziente ansioso, spiegare lentamente."
          className="w-full px-3 py-2 rounded-lg border border-amber-300 bg-white focus:outline-none focus:border-amber-500 text-sm disabled:bg-slate-50"
        />
        <div className="mt-1.5 flex items-center gap-2 text-[10px] text-amber-700">
          {doctorNoteSaving && <Loader2 size={11} className="animate-spin" />}
          {doctorNoteMeta.updated_at && <span>Ultimo salvataggio: {new Date(doctorNoteMeta.updated_at).toLocaleString("it-IT")}</span>}
        </div>
      </div>

      {/* Storico paziente: SOLO nella colonna destra (VisitSidebar), non al centro */}

      {/* Banner template ambulatorio */}
      {templateBlocks && (
        <div className="bg-brand-navy/5 border border-brand-navy/20 rounded-xl p-3 mb-4 flex flex-wrap items-center justify-between gap-2" data-testid="template-banner">
          <div className="flex flex-wrap items-center gap-2 text-xs text-brand-navy">
            <LayoutTemplate size={14} />
            <b>Modello cartella:</b>
            {readOnly ? (
              <span data-testid="template-service-label">{serviceLabel(v.service || "generale")}</span>
            ) : (
              <select
                data-testid="template-service-select"
                value={pendingService || v.service || "generale"}
                onChange={(e) => setPendingService(e.target.value === (v.service || "generale") ? null : e.target.value)}
                className="px-2 py-1 rounded-lg border border-brand-navy/20 bg-white text-xs font-bold text-brand-navy"
                title="Cambia il modello cartella / ambulatorio per questa bozza"
              >
                {!SERVICES.some((s) => s.id === (v.service || "generale")) && (
                  <option value={v.service || "generale"}>{serviceLabel(v.service || "generale")}</option>
                )}
                {SERVICES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            )}
            {pendingService && !readOnly && (
              <span className="flex flex-wrap items-center gap-1.5" data-testid="service-change-confirm-bar">
                <span className="text-amber-700 font-bold">Cambiare modello in “{serviceLabel(pendingService)}”? I campi compilati non verranno toccati.</span>
                <button
                  data-testid="service-change-confirm"
                  onClick={() => { const s = pendingService; setPendingService(null); changeService(s); }}
                  className="px-2 py-1 rounded-lg bg-brand-navy text-white font-bold hover:bg-brand-navy/90"
                >Conferma</button>
                <button
                  data-testid="service-change-cancel"
                  onClick={() => setPendingService(null)}
                  className="px-2 py-1 rounded-lg border border-slate-300 bg-white text-slate-600 font-bold hover:bg-slate-50"
                >Annulla</button>
              </span>
            )}
            <span>· {templateBlocks.length} sezioni · {templateDiag.length} diagnostiche di default</span>
          </div>
          <button onClick={() => nav("/impostazioni")} className="text-xs text-brand-azure hover:underline font-bold" data-testid="open-template-settings">⚙ Personalizza modello</button>
        </div>
      )}

      {/* Phase 29C: Precedente visita — visualizza + importa tutto */}
      {v?.id && showBlock("anamnesi") && (
        <PreviousVisitBlock
          visitId={v.id}
          readOnly={readOnly}
          onImport={(shape, parts = { anamnesi: true, refrazione: true, esame: true, terapia: true }) => {
            if (!shape) return;
            const REFR_KEYS = new Set(["visus_naturale", "visus_corretto", "posizione_refrazione", "autoref_no_ciclo", "autoref_ciclo", "autoref_no_ciclo_sph", "autoref_no_ciclo_cyl", "autoref_no_ciclo_axis", "autoref_ciclo_sph", "autoref_ciclo_cyl", "autoref_ciclo_axis", "sfera", "cilindro", "asse", "add"]);
            if (parts.anamnesi) {
              const anam = (shape.anamnesi || "").trim();
              const hasMaster = anamnesiSource === "master" && (anamnesi || "").trim();
              if (anam && !hasMaster && !(anamnesi || "").trim()) {
                setAnamnesi(anam);
              }
              if (shape.anamnesi_structured && typeof shape.anamnesi_structured === "object" && Object.keys(anamnesiStructured || {}).length === 0) {
                setAnamnesiStructured(shape.anamnesi_structured);
              }
            }
            const cleanEye = (src) => {
              const out = {};
              Object.entries(src || {}).forEach(([k, val]) => {
                if (val === null || val === undefined || String(val).trim() === "") return;
                const isRefr = REFR_KEYS.has(k);
                if ((isRefr && parts.refrazione) || (!isRefr && parts.esame)) out[k] = val;
              });
              return out;
            };
            const odNew = cleanEye(shape.OD);
            const osNew = cleanEye(shape.OS);
            if (!isEyeExcluded("OD")) setOD((prev) => ({ ...prev, ...odNew }));
            if (!isEyeExcluded("OS")) setOS((prev) => ({ ...prev, ...osNew }));
            if (parts.terapia) {
              if (shape.diagnosi_precedente) setDiagnosi(shape.diagnosi_precedente);
              if (shape.terapia_precedente) setTerapia(shape.terapia_precedente);
              if (shape.protocollo_precedente) setBehavioralProtocol(shape.protocollo_precedente);
            }
          }}
        />
      )}

      {/* Phase 31B: Banner suggerimento aggiornamento anamnesi master (IA) */}
      {anamnesiSuggested && !readOnly && (
        <div
          className="bg-amber-50 border border-amber-300 rounded-xl p-4 mb-4 shadow-sm"
          data-testid="anamnesi-suggest-banner"
          role="alert"
        >
          <div className="flex items-start gap-2 mb-2">
            <span className="text-amber-600 text-lg leading-none" aria-hidden>⚠</span>
            <div className="flex-1">
              <div className="font-extrabold text-amber-900 text-sm" data-testid="anamnesi-suggest-title">
                Nuove informazioni anamnestiche rilevate
              </div>
              <div className="text-xs text-amber-800 mt-1">
                L&apos;IA ha rilevato informazioni che potrebbero aggiornare l&apos;anamnesi stabile
                del paziente. Vuoi salvarle nell&apos;anamnesi paziente?
              </div>
            </div>
          </div>
          <div
            className="bg-white border border-amber-200 rounded-lg p-3 mb-3 text-sm text-slate-700 whitespace-pre-wrap"
            data-testid="anamnesi-suggest-content"
          >
            {anamnesiSuggested}
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={() => setAnamnesiSuggested("")}
              className="text-xs font-bold text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded"
              data-testid="anamnesi-suggest-ignore"
              disabled={anamnesiSuggestSaving}
            >
              Ignora
            </button>
            <button
              type="button"
              disabled={anamnesiSuggestSaving}
              onClick={async () => {
                if (!anamnesiSuggested.trim() || !v) return;
                setAnamnesiSuggestSaving(true);
                try {
                  const { data: am } = await api.put("/medico/patient-anamnesi", {
                    fiscal_code: v.patient_fiscal_code,
                    name: v.patient_name,
                    surname: v.patient_surname,
                    birth: v.patient_birth,
                    anamnesi: anamnesiSuggested.trim(),
                    source: "ai_suggestion_banner",
                    visit_id: v.id,
                  });
                  setAnamnesiMaster({ anamnesi: am.anamnesi, updated_at: am.updated_at, source: "master" });
                  // Aggiorna anche il campo visita corrente per coerenza
                  setAnamnesi(anamnesiSuggested.trim());
                  setAnamnesiSource("master");
                  setAnamnesiSuggested("");
                  toast.success("Anamnesi paziente aggiornata");
                } catch (e) {
                  toast.error(apiErr(e, "Errore aggiornamento anamnesi paziente"));
                } finally {
                  setAnamnesiSuggestSaving(false);
                }
              }}
              className="text-xs font-bold text-white bg-amber-600 hover:bg-amber-700 px-4 py-1.5 rounded inline-flex items-center gap-1.5 disabled:opacity-60"
              data-testid="anamnesi-suggest-apply"
            >
              {anamnesiSuggestSaving ? "Aggiornamento…" : "Aggiorna anamnesi paziente"}
            </button>
          </div>
        </div>
      )}

      {/* Controlli sezioni richiudibili */}
      <div className="flex items-center justify-end gap-2 mb-2" data-testid="sections-controls">
        <button type="button" data-testid="sections-open-all" onClick={() => setAllSections(true)} className="text-[11px] font-bold text-brand-azure hover:bg-brand-azure/10 px-2.5 py-1 rounded-lg flex items-center gap-1"><ChevronsDown size={12} /> Apri tutte</button>
        <button type="button" data-testid="sections-close-all" onClick={() => setAllSections(false)} className="text-[11px] font-bold text-slate-500 hover:bg-slate-100 px-2.5 py-1 rounded-lg flex items-center gap-1"><ChevronsUp size={12} /> Chiudi tutte</button>
      </div>

      {/* SEZIONE ANAMNESI (testo master + strutturata a toggle) */}
      {showBlock("anamnesi") && (
      <CollapsibleSection id="anamnesi" title="Anamnesi" icon={ClipboardList} open={openSections.anamnesi} onToggle={toggleSection} accent="#2C6E91">
      <div className="bg-white rounded-xl" data-testid="anamnesi-block">
        <div className="flex items-center justify-between mb-1.5 flex-wrap gap-2">
          <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Anamnesi paziente</label>
          <div className="flex items-center gap-2 flex-wrap">
            {anamnesiSource === "master" && (
              <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded" data-testid="anamnesi-imported-master">
                Importata dall'anamnesi paziente
              </span>
            )}
            {anamnesiSource === "last_visit" && (
              <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 px-2 py-0.5 rounded" data-testid="anamnesi-imported-last">
                Importata dall'ultima visita
              </span>
            )}
            {anamnesiMaster?.updated_at && (
              <span className="text-[10px] text-slate-500" data-testid="anamnesi-master-date">
                Master aggiornata il {new Date(anamnesiMaster.updated_at).toLocaleDateString("it-IT")}
              </span>
            )}
          </div>
        </div>
        <textarea
          data-testid="anamnesi"
          disabled={readOnly}
          rows={4}
          value={anamnesi}
          onChange={(e) => setAnamnesi(e.target.value)}
          style={{ lineHeight: "1.55" }}
          className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] focus:outline-none focus:border-brand-azure disabled:bg-slate-50 text-sm leading-relaxed resize-none break-words whitespace-pre-wrap"
        />
        {!readOnly && (
          <div className="flex flex-wrap items-center gap-2 mt-2">
            {!anamnesi.trim() && (anamnesiMaster?.anamnesi || "").trim() && (
              <button
                data-testid="anamnesi-import"
                onClick={() => { setAnamnesi(anamnesiMaster.anamnesi); setAnamnesiSource(anamnesiMaster.source || "master"); toast.success("Anamnesi precedente importata"); }}
                className="text-xs font-bold text-brand-azure hover:text-brand-navy bg-brand-azure/10 hover:bg-brand-azure/20 px-3 py-1.5 rounded-lg"
              >
                Importa anamnesi precedente
              </button>
            )}
            <button
              data-testid="anamnesi-save-master"
              disabled={anamnesiSaving || !anamnesi.trim()}
              onClick={async () => {
                setAnamnesiSaving(true);
                try {
                  const { data: am } = await api.put("/medico/patient-anamnesi", {
                    fiscal_code: v.patient_fiscal_code,
                    name: v.patient_name,
                    surname: v.patient_surname,
                    birth: v.patient_birth,
                    anamnesi,
                    source: "manual_button",
                    visit_id: v.id,
                  });
                  setAnamnesiMaster({ anamnesi: am.anamnesi, updated_at: am.updated_at, source: "master" });
                  toast.success("Salvata come anamnesi paziente: verrà riproposta nelle visite future");
                } catch (e) { toast.error(apiErr(e, "Errore salvataggio anamnesi paziente")); }
                finally { setAnamnesiSaving(false); }
              }}
              className="text-xs font-bold text-emerald-700 hover:text-emerald-900 bg-emerald-50 hover:bg-emerald-100 px-3 py-1.5 rounded-lg disabled:opacity-50"
            >
              {anamnesiSaving ? "Salvataggio…" : "Aggiorna anamnesi paziente"}
            </button>
          </div>
        )}
      </div>
      <div className="mt-4 border-t border-slate-100 pt-4">
        <AnamnesiStructured value={anamnesiStructured} onChange={setAnamnesiStructured} readOnly={readOnly} />
      </div>
      </CollapsibleSection>
      )}

      {/* Banner: referto corretto post-invio → reinviare */}
      {isClosed && v.report_needs_resend && (
        <div className="bg-orange-50 border border-orange-300 rounded-xl p-4 mb-4 flex flex-wrap items-center justify-between gap-3" data-testid="report-resend-banner">
          <div className="flex items-start gap-2">
            <Send className="text-orange-700" size={18} />
            <div>
              <div className="font-extrabold text-sm text-orange-900">Referto corretto — reinvia al paziente</div>
              <div className="text-xs text-orange-700 mt-0.5">Hai modificato il referto entro la finestra dei 60 min e il paziente aveva già ricevuto la versione precedente. Reinvialo via email/SMS per allinearlo.</div>
            </div>
          </div>
          <button data-testid="open-send-resend" onClick={() => setSendOpen(true)} className="bg-orange-600 hover:bg-orange-700 text-white font-bold text-xs px-3 py-2 rounded-lg flex items-center gap-1.5">
            <Send size={12} /> Reinvia referto aggiornato
          </button>
        </div>
      )}

      {/* Banner finestra modifica 60 min */}
      {isClosed && editStatus && (
        <div className={`rounded-xl border p-4 mb-4 flex flex-wrap items-center justify-between gap-3 ${inEditWindow ? "bg-amber-50 border-amber-300" : "bg-slate-100 border-slate-300"}`} data-testid="report-edit-window-banner">          <div className="flex items-start gap-2">
            <Lock className={inEditWindow ? "text-amber-700" : "text-slate-500"} size={18} />
            <div>
              <div className={`font-extrabold text-sm ${inEditWindow ? "text-amber-900" : "text-slate-700"}`}>
                {inEditWindow ? `Modificabile ancora per ${editStatus.minutes_remaining} min` : "Referto bloccato (finestra di modifica scaduta)"}
              </div>
              <div className="text-xs text-slate-600 mt-0.5">
                {inEditWindow
                  ? "Puoi modificare referto, diagnosi, terapia e protocollo entro 60 minuti dalla chiusura. Trascorso questo tempo, usa l'addendum."
                  : "Per aggiungere informazioni o correzioni usa la sezione Addendum qui sotto."}
              </div>
            </div>
          </div>
          {inEditWindow && !editReportMode && (
            <button data-testid="enter-edit-report-mode" onClick={() => setEditReportMode(true)} className="bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs px-3 py-2 rounded-lg">
              Modifica referto
            </button>
          )}
          {inEditWindow && editReportMode && (
            <div className="flex flex-wrap items-center gap-2">
              <input data-testid="edit-report-reason" value={editReason} onChange={(e) => setEditReason(e.target.value)} placeholder="Motivo modifica (opz.)" className="px-3 py-1.5 rounded-lg border border-amber-300 text-xs bg-white" />
              <button data-testid="save-report-edit" onClick={saveReportEdit} disabled={savingReportEdit} className="bg-brand-navy hover:bg-brand-navyDark text-white font-bold text-xs px-3 py-2 rounded-lg flex items-center gap-1.5 disabled:opacity-60">
                {savingReportEdit && <Loader2 size={12} className="animate-spin" />} Salva
              </button>
              <button data-testid="cancel-report-edit" onClick={() => { setEditReportMode(false); setEditReason(""); load(); }} className="text-xs text-slate-600 hover:text-red-600 px-2 py-1.5">Annulla</button>
            </div>
          )}
        </div>
      )}

      {/* Pulsante "Tutto sano" + scorciatoie default cartella */}
      {!readOnly && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 mb-4 flex flex-wrap items-center justify-between gap-3" data-testid="tutto-sano-block">
          <div className="text-xs text-emerald-900">
            <b>Macro rapide:</b> compila i campi clinici (Tono, Segmento anteriore, Annessi, Pupille, Fundus) con i tuoi default. <span className="font-bold">Il visus naturale/corretto resta sempre manuale</span>.
          </div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="tutto-sano-overwrite" onClick={() => { if (window.confirm("Compilare anche i campi già valorizzati con i default 'tutto sano'?")) applyTuttoSano(true); }} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs px-3 py-2 rounded-lg">
              Tutto sano
            </button>
            <button data-testid="tutto-sano-fill" onClick={() => applyTuttoSano(false)} className="bg-white border border-emerald-600 text-emerald-700 hover:bg-emerald-100 font-bold text-xs px-3 py-2 rounded-lg">
              Tutto sano solo i vuoti
            </button>
            <button data-testid="open-defaults-settings" onClick={() => nav("/impostazioni")} className="text-xs text-emerald-700 hover:underline">
              ⚙ Personalizza default
            </button>
          </div>
        </div>
      )}

      {/* SEZIONE REFRAZIONE E PRESCRIZIONE OCCHIALI */}
      {(() => {
        const REFRACTIVE_TOP = ["visus_naturale", "visus_corretto", "posizione_refrazione", "autoref_no_ciclo", "autoref_ciclo"];
        const topFields = eyeFields.filter((f) => REFRACTIVE_TOP.includes(f.k));
        const hasTonoBlock = eyeFields.some((f) => f.k === "tono");
        const bottomFields = eyeFields.filter((f) => !REFRACTIVE_TOP.includes(f.k) && !(hasTonoBlock && (f.k === "tono" || f.k === "pachimetria")));
        const exclusionBar = (
          <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="eye-exclusion-bar">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Escludi occhio:</span>
            {["OD", "OS"].map((side) => {
              const exc = isEyeExcluded(side);
              return (
                <button key={side} type="button" disabled={readOnly} data-testid={`exclude-${side.toLowerCase()}`}
                  onClick={() => toggleEyeExclusion(side)}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border-2 text-[11px] font-bold transition disabled:opacity-50 ${exc ? "bg-slate-700 border-slate-700 text-white" : "bg-white border-[#E2E8F0] text-slate-500 hover:border-slate-400"}`}>
                  <EyeOff size={11} /> {side}{exc ? ` escluso${(eyeExcluded[side] || {}).reason ? ` (${(eyeExcluded[side] || {}).reason})` : ""}` : ""}
                </button>
              );
            })}
            <span className="text-[10px] text-slate-400">occhio spento, protesi, bendato, non valutabile… L&apos;IA e la copia OD/OS rispettano l&apos;esclusione.</span>
          </div>
        );
        return (
          <>
            <CollapsibleSection id="refrazione" title="Refrazione e Prescrizione Occhiali" icon={Glasses} open={openSections.refrazione} onToggle={toggleSection} accent="#E8804C">
              {exclusionBar}
              {/* A-B-C: usa occhiali? → lenti in uso (L/I/V) + Visus CSL */}
              <RefrazioneAvanzataBlock part="lenti" prescrizione={prescrizioneOcchiali} setPrescrizione={setPrescrizioneOcchiali} readOnly={readOnly} eyeExcluded={eyeExcluded} />
              {/* NB: part="prescrizione" (blocco nuovo L/I/V) RIMOSSO: duplicava "Prescrizione Occhiali".
                  L'unico blocco prescrizione è PrescrizioneOcchialiBlock (sotto), potenziato con
                  rotelline 0.25, visus corretto e Add/SF intermedio+vicino. */}
              {/* D: Autoref (senza/con ciclo) + visus naturale/corretto — sempre visibili */}
              {topFields.length > 0 && (
                <EyePairForm fields={topFields} OD={OD} OS={OS} setOD={setOD} setOS={setOS} readOnly={readOnly} isEyeExcluded={isEyeExcluded} eyeExcluded={eyeExcluded} onCopyAll={copyTo} onCopyField={copyField} idSuffix="-refr" />
              )}
              {/* Ordine clinico: Visus naturale → Visus corretto → Visus per vicino (PV) → Amsler → Prescrizione Occhiali */}
              {showBlock("visus_vicino") && (
                <EyeMiniPair title="Visus per vicino (PV)" accent="#E8804C" testid="pv-libero-block">
                  <PvLiberoEye side="OD" data={OD} set={setOD} disabled={readOnly || isEyeExcluded("OD")} />
                  <PvLiberoEye side="OS" data={OS} set={setOS} disabled={readOnly || isEyeExcluded("OS")} />
                </EyeMiniPair>
              )}
              {(showBlock("amsler") || templateDiag.includes("amsler")) && (
                <EyeMiniPair title="Test di Amsler" accent="#0E7490" testid="amsler-block">
                  <AmslerEye side="OD" data={OD} set={setOD} disabled={readOnly || isEyeExcluded("OD")} />
                  <AmslerEye side="OS" data={OS} set={setOS} disabled={readOnly || isEyeExcluded("OS")} />
                </EyeMiniPair>
              )}
              {/* E: blocco nuovo L/I/V rimosso (duplicato) — vedi nota sopra */}
              {/* F: Prescrizione Occhiali unica (SPH/CYL/AXIS/PRISMA/BASE/TABO/Trasponi + rotelline/visus/Add) */}
              {showBlock("prescrizione_occhiali") && (
                <PrescrizioneOcchialiBlock
                  od={OD}
                  os={OS}
                  prescrizione={prescrizioneOcchiali}
                  setPrescrizione={setPrescrizioneOcchiali}
                  readOnly={readOnly}
                />
              )}
            </CollapsibleSection>

            <CollapsibleSection id="esame" title="Esame Obiettivo" icon={Stethoscope} open={openSections.esame} onToggle={toggleSection} accent="#2C4667">
              <ExamPresetChips onApply={applyExamPreset} readOnly={readOnly} eyeExcluded={eyeExcluded} />
              {hasTonoBlock && (
                <EyeMiniPair title="Tono (IOP) e Pachimetria" accent="#2C4667" testid="tono-pachimetria-block">
                  <TonoPachiEye side="OD" data={OD} set={setOD} disabled={readOnly || isEyeExcluded("OD")} />
                  <TonoPachiEye side="OS" data={OS} set={setOS} disabled={readOnly || isEyeExcluded("OS")} />
                </EyeMiniPair>
              )}
              {bottomFields.length > 0 && (
                <EyePairForm fields={bottomFields} OD={OD} OS={OS} setOD={setOD} setOS={setOS} readOnly={readOnly} isEyeExcluded={isEyeExcluded} eyeExcluded={eyeExcluded} onCopyAll={copyTo} onCopyField={copyField} idSuffix="-eo" />
              )}
              <MotilitaForm
                specialized={specialized}
                setSpecialized={setSpecialized}
                showBlock={showBlock}
                readOnly={readOnly}
                onAttachGaze9={onAttachGaze9}
                gaze9Files={diagnosticaAllegati["diag_gaze9_foto"] || []}
              />
              <SpecializedBlocks
                showBlock={showBlock}
                v={v}
                readOnly={readOnly}
                ouCustom={ouCustom}
                setOuCustom={setOuCustom}
              />
            </CollapsibleSection>
          </>
        );
      })()}

      {/* Picker modal */}
      {diagPickerOpen && (
        <DiagnosticaPicker
          options={DIAGNOSTICA_OPTIONS}
          selected={diagnostica}
          favorites={templateDiag}
          onAdd={(id) => { setDiagnostica((p) => p.includes(id) ? p : [...p, id]); setDiagPickerOpen(false); }}
          onClose={() => setDiagPickerOpen(false)}
        />
      )}

      {/* SEZIONE DIAGNOSI E TERAPIA */}
      {true && (
      <CollapsibleSection id="diagnosi" title="Diagnosi e Terapia" icon={FileText} open={openSections.diagnosi} onToggle={toggleSection} accent="#0E7490">
      {/* Proposta: terapia riferita in corso dall'anamnesi (il medico conferma l'inserimento) */}
      {!readOnly && !terapiaProposalDismissed && (anamnesiStructured?.terapia_oculare?.v === "si") && (anamnesiStructured?.terapia_oculare?.items || []).some((it) => (it.farmaco || "").trim()) && !(terapia || "").includes("Terapia riferita in corso") && (
        <div className="bg-sky-50 border border-sky-300 rounded-xl p-3 mb-4 flex flex-wrap items-center justify-between gap-2" data-testid="terapia-in-corso-proposal">
          <div className="text-xs text-sky-900">
            <b>Terapia riferita/in corso (da anamnesi):</b>{" "}
            {(anamnesiStructured.terapia_oculare.items || []).filter((it) => (it.farmaco || "").trim()).map((it) => `${it.farmaco}${it.occhio ? ` ${it.occhio}` : ""}${it.frequenza ? ` ${it.frequenza}` : ""}`).join(" · ")}
          </div>
          <div className="flex gap-2">
            <button data-testid="terapia-proposal-apply" onClick={() => {
              const items = (anamnesiStructured.terapia_oculare.items || []).filter((it) => (it.farmaco || "").trim());
              const lines = items.map((it) => `- ${it.farmaco}${it.drug_ref?.principle ? ` (${it.drug_ref.principle})` : ""}${it.occhio ? ` (${it.occhio})` : ""}${it.frequenza ? ` — ${it.frequenza}` : ""}${it.note ? ` — ${it.note}` : ""}`);
              setTerapia((prev) => {
                const newLines = lines.filter((ln) => !(prev || "").includes(ln));
                if (!newLines.length && (prev || "").includes("Terapia riferita in corso")) return prev;
                return `${prev ? prev + "\n" : ""}Terapia riferita in corso:\n${newLines.join("\n")}`;
              });
              // Farmaci collegati al prontuario → voci strutturate in terapia_items (coerenti con DrugPrescriber)
              const structured = items.filter((it) => it.drug_ref).map((it) => {
                const freqNum = parseInt((it.frequenza || "").match(/\d+/)?.[0] || "", 10);
                return {
                  atc: it.drug_ref.atc, principle: it.drug_ref.principle, brand: it.drug_ref.brand,
                  category: it.drug_ref.category || "", form: it.drug_ref.form || "",
                  route: it.drug_ref.default_posology?.route || "topico oculare",
                  dose: it.drug_ref.default_posology?.dose || "",
                  frequency_per_day: Number.isFinite(freqNum) ? freqNum : (it.drug_ref.default_posology?.frequency_per_day || null),
                  days: null,
                  instructions: `Terapia riferita in corso${it.occhio ? ` (${it.occhio})` : ""}${it.frequenza ? ` — ${it.frequenza}` : ""}`,
                  extra_notes: it.note || "",
                  verified: !!it.drug_ref.verified,
                  source: it.drug_ref.source || "",
                  from_anamnesi: true,
                  confirmed_at: new Date().toISOString(),
                };
              });
              if (structured.length) {
                setTerapiaItems((p) => {
                  const existing = new Set(p.map((x) => `${x.atc}|${x.brand}`));
                  return [...p, ...structured.filter((s) => !existing.has(`${s.atc}|${s.brand}`))];
                });
              }
              toast.success(structured.length
                ? `Terapia riferita inserita (${structured.length} farmac${structured.length === 1 ? "o" : "i"} dal prontuario): verifica prima di prescrivere`
                : "Terapia riferita inserita: verifica e modifica prima di prescrivere");
            }} className="bg-sky-600 hover:bg-sky-700 text-white text-[11px] font-bold px-3 py-1.5 rounded-lg">Inserisci in Terapia</button>
            <button data-testid="terapia-proposal-dismiss" onClick={() => setTerapiaProposalDismissed(true)} className="text-[11px] font-bold text-slate-500 hover:bg-slate-100 px-2 py-1.5 rounded-lg">Ignora</button>
          </div>
        </div>
      )}
      {true && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          {showBlock("diagnosi") && (
            <AiInlineField
              kind="diagnosi"
              label="Diagnosi"
              value={diagnosi}
              onChange={setDiagnosi}
              readOnly={readOnly}
              context={{ anamnesi, OD, OS, diagnosi, terapia }}
              testid="field-diagnosi"
              rows={4}
            />
          )}
          {showBlock("terapia") && (
            <AiInlineField
              kind="terapia"
              label="Terapia"
              value={terapia}
              onChange={setTerapia}
              readOnly={readOnly}
              context={{ anamnesi, OD, OS, diagnosi, terapia }}
              testid="field-terapia"
              rows={4}
            />
          )}
        </div>
      )}

      {/* Terapia · Farmaci da prontuario (selezione → inserisce in Terapia testo libero) */}
      {showBlock("terapia") && (
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-5 mb-4" data-testid="terapia-items-block">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Pill className="text-brand-azure" size={18} />
              <h3 className="font-extrabold text-brand-navy">Farmaci da prontuario</h3>
              <span className="text-xs text-slate-500">({terapiaItems.length})</span>
            </div>
            {!readOnly && (
              <button data-testid="open-drug-prescriber" onClick={() => setDrugOpen(true)} className="bg-brand-azure hover:bg-brand-azureDark text-white font-bold text-xs px-3 py-2 rounded-lg flex items-center gap-1.5"><Plus size={13} /> Aggiungi farmaco</button>
            )}
          </div>
          {terapiaItems.length === 0 ? (
            <div className="text-xs text-slate-500 italic py-3">Nessun farmaco selezionato. Cerca un farmaco — comparirà automaticamente nel campo Terapia qui sopra.</div>
          ) : (
            <ul className="space-y-2" data-testid="terapia-items-list">
              {terapiaItems.map((it, idx) => <TerapiaItem key={idx} item={it} idx={idx} onRemove={removeDrug} readOnly={readOnly} />)}
            </ul>
          )}
          <div className="mt-2 text-[10px] text-slate-400 italic flex items-start gap-1.5">
            <ShieldCheck size={10} className="mt-0.5" />
            Le posologie sono linee guida da RCP/AIFA. Verifica e adatta al paziente prima della prescrizione.
          </div>
        </div>
      )}

      {/* Prossimo controllo (visibile sia medico che ortottica/refrazione) */}
      {true && (
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-5 mb-4" data-testid="prossimo-controllo-block">
          <label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
            <History size={12} /> Prossimo controllo / Controllo consigliato
          </label>
          <div className="mt-2 flex flex-wrap gap-1.5 mb-2" data-testid="prossimo-controllo-shortcuts">
            {[
              "Si consiglia prossimo controllo tra 3 mesi salvo insorgenza di nuovi disturbi",
              "Si consiglia prossimo controllo tra 6 mesi salvo insorgenza di nuovi disturbi",
              "Si consiglia prossimo controllo tra 12 mesi salvo insorgenza di nuovi disturbi",
              "Si consiglia prossimo controllo tra 18 mesi salvo insorgenza di nuovi disturbi",
              "Si consiglia prossimo controllo tra 24 mesi salvo insorgenza di nuovi disturbi",
            ].map((s) => (
              <button
                key={s}
                type="button"
                disabled={readOnly}
                onClick={() => setProssimoControllo(s)}
                data-testid={`prossimo-controllo-quick-${s.match(/(\d+)/)?.[1] || "?"}`}
                className="px-2 py-1 rounded-full bg-brand-azure/10 hover:bg-brand-azure/20 text-brand-navy text-[11px] font-bold disabled:opacity-40"
              >
                {s.match(/(\d+) mesi/)?.[0] || s}
              </button>
            ))}
          </div>
          <AutoTextarea
            data-testid="prossimo-controllo-input"
            value={prossimoControllo}
            onChange={(e) => setProssimoControllo(e.target.value)}
            placeholder="Es. Si consiglia prossimo controllo tra 6 mesi salvo insorgenza di nuovi disturbi"
            disabled={readOnly}
            minRows={2}
            className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm"
          />
          {prossimoControllo && (
            <div className="mt-1 text-[10px] text-slate-500 italic">Comparirà nel PDF/referto come ultima sezione.</div>
          )}
        </div>
      )}

      {/* Protocollo comportamentale (con IA inline). Tag: unico sistema "Tag paziente" in alto. */}
      {showBlock("protocollo_suggerito") && (
        <div className="mb-4">
          <ProtocolSuggestPanel
            hashtags={clinicalHashtags}
            setHashtags={setClinicalHashtags}
            value={behavioralProtocol}
            onChange={setBehavioralProtocol}
            readOnly={readOnly}
            diagnosi={diagnosi}
            context={{ anamnesi, OD, OS, diagnosi, terapia }}
            testid="behavioral-protocol"
          />
        </div>
      )}
      </CollapsibleSection>
      )}

      {/* SEZIONE PROGRAMMA CHIRURGIA */}
      <CollapsibleSection id="chirurgia" title="Programma Chirurgia" icon={Scissors} open={openSections.chirurgia} onToggle={toggleSection} accent="#B91C1C">
        <SurgeryProgramBlock
          visitId={vid}
          readOnly={readOnly}
          sites={sites}
          phone={anagrafica.patient_phone}
          email={anagrafica.patient_email}
          patient={{
            fiscal_code: anagrafica.patient_fiscal_code,
            name: anagrafica.patient_name,
            surname: anagrafica.patient_surname,
            birth: anagrafica.patient_birth,
          }}
        />
      </CollapsibleSection>

      {/* SEZIONE ALLEGATI / DIAGNOSTICA */}
      <CollapsibleSection id="allegati" title="Allegati ed Esami Strumentali" icon={Paperclip} open={openSections.allegati} onToggle={toggleSection} accent="#6D28D9">
      {/* Diagnostica — chiusa di default, si apre via picker */}
      <div className="bg-white rounded-xl border border-[#E2E8F0] p-5 mb-4" data-testid="diagnostica-block">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Microscope className="text-brand-azure" size={18} />
            <h3 className="font-extrabold text-brand-navy">Diagnostica</h3>
            <span className="text-[11px] text-slate-500">{diagnostica.length} esami aggiunti{templateDiag.length ? ` · ${templateDiag.length} preferiti modello` : ""}</span>
          </div>
          {!readOnly && (
            <button
              data-testid="diag-add-open"
              onClick={() => setDiagPickerOpen(true)}
              className="inline-flex items-center gap-1.5 bg-brand-azure hover:bg-brand-navyDark text-white font-bold text-xs px-3 py-2 rounded-lg shadow-sm"
            >
              <Plus size={13} /> Aggiungi diagnostica
            </button>
          )}
        </div>
        {diagnostica.length === 0 ? (
          <div className="text-center text-slate-500 text-xs italic py-6 border-2 border-dashed border-[#E2E8F0] rounded-lg" data-testid="diag-empty-state">
            Nessun esame diagnostico aggiunto. Premi <b>Aggiungi diagnostica</b> per aprire il menu degli esami.
          </div>
        ) : (
          <div className="space-y-3" data-testid="diagnostica-referti">
            {diagnostica.map((dId) => {
              const def = DIAGNOSTICA_OPTIONS.find((o) => o.id === dId) || { id: dId, label: dId };
              const recData = (diagnosticaReferti[dId] && typeof diagnosticaReferti[dId] === "object" && !Array.isArray(diagnosticaReferti[dId]))
                ? diagnosticaReferti[dId]
                : { referto: typeof diagnosticaReferti[dId] === "string" ? diagnosticaReferti[dId] : "", conclusioni: "", side: "OU", status: "bozza" };
              const updateRec = (patch) => setDiagnosticaReferti((p) => ({ ...p, [dId]: { ...recData, ...patch } }));
              const isCompleted = recData.status === "completato";
              return (
                <div key={dId} className={`border rounded-lg p-3 ${isCompleted ? "border-emerald-300 bg-emerald-50/30" : "border-[#E2E8F0]"}`} data-testid={`diag-referto-${dId}`}>
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-brand-navy">{def.label}</span>
                      <select
                        disabled={readOnly}
                        value={recData.side || "OU"}
                        onChange={(e) => updateRec({ side: e.target.value })}
                        data-testid={`diag-side-${dId}`}
                        className="text-[10px] px-1.5 py-0.5 rounded border border-[#E2E8F0] bg-white font-bold"
                      >
                        <option value="OD">OD</option>
                        <option value="OS">OS</option>
                        <option value="OU">OU</option>
                      </select>
                      <select
                        disabled={readOnly}
                        value={recData.status || "bozza"}
                        onChange={(e) => updateRec({ status: e.target.value })}
                        data-testid={`diag-status-${dId}`}
                        className={`text-[10px] px-1.5 py-0.5 rounded border font-bold ${isCompleted ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-amber-100 text-amber-800 border-amber-300"}`}
                      >
                        <option value="bozza">Bozza</option>
                        <option value="completato">Completato</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-2">
                      {!readOnly && (
                        <label className="text-[11px] text-brand-azure hover:underline cursor-pointer flex items-center gap-1">
                          <Plus size={11} /> Allega file
                          <input type="file" data-testid={`diag-attach-${dId}`} className="hidden" onChange={(e) => onAttachDiag(dId, e.target.files?.[0])} accept="image/*,application/pdf" />
                        </label>
                      )}
                      {!readOnly && (
                        <button
                          data-testid={`diag-remove-${dId}`}
                          onClick={() => { if (window.confirm(`Rimuovere "${def.label}" dalla visita?`)) { setDiagnostica((p) => p.filter((x) => x !== dId)); setDiagnosticaReferti((p) => { const c = { ...p }; delete c[dId]; return c; }); } }}
                          className="text-red-500 hover:text-red-700 text-[11px] font-bold"
                          title="Rimuovi esame"
                        >
                          <XIcon size={13} />
                        </button>
                      )}
                    </div>
                  </div>
                  <AutoTextarea
                    testid={`diag-text-${dId}`}
                    disabled={readOnly}
                    rows={3}
                    value={recData.referto || ""}
                    onChange={(v) => updateRec({ referto: v })}
                    placeholder={`Referto ${def.label}: descrizione, misure, reperti…`}
                    className="mb-2"
                  />
                  <AutoTextarea
                    testid={`diag-conclusioni-${dId}`}
                    disabled={readOnly}
                    rows={2}
                    value={recData.conclusioni || ""}
                    onChange={(v) => updateRec({ conclusioni: v })}
                    placeholder={`Conclusioni / interpretazione clinica…`}
                  />
                  {Array.isArray(diagnosticaAllegati[dId]) && diagnosticaAllegati[dId].length > 0 && (
                    <div className="mt-1.5 text-[10px] text-slate-600">
                      Allegati: {diagnosticaAllegati[dId].map((a) => a.name).join(", ")}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      </CollapsibleSection>


      <DrugPrescriber open={drugOpen} onClose={() => setDrugOpen(false)} onPick={addDrug} />

      {/* P0 — Pulsante REC "Registra visita IA" sticky floating */}
      {!readOnly && (
        <VisitDictation
          visitId={vid}
          service={v.service}
          currentOD={OD}
          currentOS={OS}
          currentAnamnesi={anamnesi}
          onApply={applyDictation}
          healthyDefaults={healthyDefaults}
        />
      )}

      {/* Addendum: solo quando chiusa */}
      {v.status === "closed" && (
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-5 mb-4">
          <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5"><Plus size={14} /> Nuovo addendum</h3>
          <textarea data-testid="addendum-input" rows={3} value={addendumText} onChange={(e) => setAddendumText(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] text-sm" />
          <button data-testid="addendum-submit" onClick={addAddendum} className="mt-2 bg-brand-azure hover:bg-brand-azureDark text-white font-bold text-sm px-4 py-2 rounded-lg">Aggiungi addendum</button>
          {v.addendums?.length > 0 && (
            <ul className="mt-4 space-y-2">
              {v.addendums.map((a) => (
                <li key={a.id} className="border-l-4 border-brand-azure pl-3 py-1">
                  <div className="text-xs text-slate-500">{new Date(a.at).toLocaleString("it-IT")} · {a.author}</div>
                  <div className="text-sm text-slate-800">{a.text}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* FSE 2.0 — visibile solo per visite chiuse non archiviate */}
      {isClosed && !v.archived && (
        <div className="mb-3">
          <FseBlock vid={vid} />
        </div>
      )}

      {/* Azioni distruttive: bozza → Elimina, chiusa → Archivia, archiviata → Ripristina */}
      <div className="mb-2 flex items-center justify-end gap-2" data-testid="visit-danger-actions">
        {v.archived ? (
          <button
            data-testid="visit-restore-btn"
            onClick={() => setDestructiveAction("restore")}
            className="text-xs font-bold text-emerald-700 hover:bg-emerald-50 px-3 py-1.5 rounded-lg border border-emerald-200 flex items-center gap-1.5"
          >
            Ripristina visita
          </button>
        ) : isClosed ? (
          <button
            data-testid="visit-archive-btn"
            onClick={() => setDestructiveAction("archive")}
            className="text-xs font-bold text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg border border-red-200 flex items-center gap-1.5"
          >
            Archivia referto
          </button>
        ) : (
          <button
            data-testid="visit-delete-btn"
            onClick={() => setDestructiveAction("delete")}
            className="text-xs font-bold text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg border border-red-200 flex items-center gap-1.5"
          >
            Elimina bozza
          </button>
        )}
      </div>

      {/* Sticky actions */}
      <div className="sticky bottom-0 z-30 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-4 bg-white border-t border-[#E2E8F0] flex flex-wrap items-center justify-end gap-2">
        {/* Autosave indicator */}
        {!readOnly && (
          <div className="mr-auto flex items-center gap-1.5 text-xs" data-testid="autosave-indicator">
            {autosaveStatus === "saving" && <><Loader2 size={12} className="animate-spin text-brand-azure" /><span className="text-slate-600">Salvataggio…</span></>}
            {autosaveStatus === "saved" && <><span className="w-2 h-2 rounded-full bg-emerald-500" /><span className="text-emerald-700">Salvato{autosaveAt ? " " + new Date(autosaveAt).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : ""}</span></>}
            {autosaveStatus === "error" && <><span className="w-2 h-2 rounded-full bg-red-500" /><span className="text-red-700">Errore salvataggio — riprova</span></>}
            {autosaveStatus === "idle" && dirty && <><span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" /><span className="text-amber-700">Modifiche non salvate</span></>}
          </div>
        )}
        {/* Undo ultimo inserimento IA */}
        {!readOnly && aiSnapshot && (
          <button data-testid="undo-ai-apply" onClick={undoAiApply} className="text-xs font-bold text-amber-700 border border-amber-300 hover:bg-amber-50 px-3 py-1.5 rounded-lg flex items-center gap-1.5">
            <Undo2 size={12} /> Annulla ultimo inserimento IA
          </button>
        )}
        <button onClick={() => safeNav(() => nav("/visite"))} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100" data-testid="back-to-visits">Torna alle visite</button>
        {isClosed && (
          <>
            <button data-testid="generate-pdf" onClick={() => openPdf(false)} className="bg-white border border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white font-bold px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
              <FileText size={14} /> Genera PDF
            </button>
            <button data-testid="print-pdf" onClick={() => openPdf(true)} className="bg-white border border-brand-azure text-brand-azure hover:bg-brand-azure hover:text-white font-bold px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
              <Printer size={14} /> Stampa
            </button>
          </>
        )}
        {!readOnly && (
          <>
            <button data-testid="save-visit" onClick={save} disabled={saving} className="bg-brand-azure hover:bg-brand-azureDark text-white font-bold px-5 py-2 rounded-lg flex items-center gap-2 disabled:opacity-60">
              {saving && <Loader2 size={14} className="animate-spin" />} Salva
            </button>
            {!isClosed && (
              <button data-testid="close-visit" onClick={close} disabled={closing} className="bg-brand-navy hover:bg-brand-navyDark text-white font-bold px-5 py-2 rounded-lg flex items-center gap-2 disabled:opacity-60">
                {closing && <Loader2 size={14} className="animate-spin" />} <FileText size={14} /> Chiudi e genera referto
              </button>
            )}
          </>
        )}
        {isClosed && (
          <button data-testid="open-send" onClick={() => setSendOpen(true)} className="bg-brand-orange hover:bg-brand-orangeDark text-white font-bold px-5 py-2 rounded-lg flex items-center gap-2"><Mail size={14} /> Invia referto</button>
        )}
      </div>

      {/* Colonna destra sotto xl: renderizzata in fondo alla colonna principale (istanza unica) */}
      {!isXl && (
      <div className="mt-4">
        <VisitSidebar
          phone={anagrafica.patient_phone}
          email={anagrafica.patient_email}
          readOnly={readOnly}
          patient={{ fiscal_code: anagrafica.patient_fiscal_code, name: anagrafica.patient_name, surname: anagrafica.patient_surname, birth: anagrafica.patient_birth }}
          history={history}
          onOpenPreview={(id) => setPreviewVid(id)}
          refreshKey={v?.updated_at}
          followupText={[prossimoControllo, terapia, diagnosi].filter(Boolean).join("\n")}
          sourceVisitId={vid}
        />
      </div>
      )}
      </div>

      {/* Colonna destra sticky su desktop */}
      {isXl && (
      <div className="w-72 shrink-0 sticky top-4" data-testid="visit-right-column">
        <VisitSidebar
          phone={anagrafica.patient_phone}
          email={anagrafica.patient_email}
          readOnly={readOnly}
          patient={{ fiscal_code: anagrafica.patient_fiscal_code, name: anagrafica.patient_name, surname: anagrafica.patient_surname, birth: anagrafica.patient_birth }}
          history={history}
          onOpenPreview={(id) => setPreviewVid(id)}
          refreshKey={v?.updated_at}
          followupText={[prossimoControllo, terapia, diagnosi].filter(Boolean).join("\n")}
          sourceVisitId={vid}
        />
      </div>
      )}
      </div>

      {sendOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setSendOpen(false)}>
          <div className="bg-white rounded-xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="send-modal">
            <h3 className="text-lg font-extrabold text-brand-navy mb-1">Consegna referto</h3>
            <p className="text-xs text-slate-500 mb-3">Privacy by design: il link PDF è tokenizzato, non indovinabile. Le tue note personali sul paziente non vengono mai incluse.</p>
            <div className="space-y-2">
              <button data-testid="send-email" disabled={!v.patient_email} onClick={() => sendReport("email")} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-[#E2E8F0] hover:bg-brand-azure/10 disabled:opacity-50 disabled:cursor-not-allowed">
                <Mail className="text-brand-azure" /> <div className="text-left flex-1"><div className="font-bold text-brand-navy">Invia referto via email</div><div className="text-xs text-slate-500">{v.patient_email || "(email mancante — non disponibile)"}</div></div>
              </button>
              <button data-testid="send-sms" disabled={!v.patient_phone || !msgConfig?.sms_configured} onClick={() => sendReport("sms")} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-[#E2E8F0] hover:bg-brand-azure/10 disabled:opacity-50 disabled:cursor-not-allowed">
                <Phone className="text-brand-azure" /> <div className="text-left flex-1"><div className="font-bold text-brand-navy">Invia link PDF via SMS</div><div className="text-xs text-slate-500">{!msgConfig?.sms_configured ? "SMS non configurato — canale disattivato" : (v.patient_phone || "(telefono mancante — non disponibile)")}</div></div>
              </button>
              {msgConfig?.sms_configured && (
              <button data-testid="send-both" disabled={!v.patient_email || !v.patient_phone} onClick={() => sendReport("both")} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 border-brand-orange text-brand-orange hover:bg-brand-orange hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition">
                <Mail /> <Phone /> <div className="text-left flex-1"><div className="font-bold">Invia email + SMS</div><div className="text-xs opacity-70">Entrambi i canali contemporaneamente</div></div>
              </button>
              )}
              <button data-testid="send-print" onClick={() => sendReport("print")} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-[#E2E8F0] hover:bg-brand-azure/10">
                <Printer className="text-brand-azure" /> <div className="text-left flex-1"><div className="font-bold text-brand-navy">Stampa referto</div><div className="text-xs text-slate-500">Registra evento in audit</div></div>
              </button>
            </div>
            {v.report_link && (
              <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs">
                <div className="font-bold text-emerald-800 flex items-center gap-1.5"><ShieldCheck size={12} /> Link sicuro attivo</div>
                <div className="text-emerald-700 mt-1 break-all font-mono">{v.report_link}</div>
                {v.report_delivery_status && <div className="text-emerald-700 mt-1">Stato consegna: <b>{v.report_delivery_status}</b></div>}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Drawer anteprima visita (storico paziente) */}
      <VisitPreviewDrawer
        visitId={previewVid}
        open={!!previewVid}
        onClose={() => setPreviewVid(null)}
        onOpenFull={(id) => { setPreviewVid(null); safeNav(() => nav(`/visite/${id}`)); }}
      />

      {/* Modal conferma uscita (anti-perdita dati) */}
      {exitConfirmOpen && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" data-testid="exit-confirm-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                <AlertTriangle className="text-amber-600" size={20} />
              </div>
              <div>
                <h3 className="font-extrabold text-brand-navy text-lg">Hai modifiche non salvate</h3>
                <p className="text-sm text-slate-600 mt-1">Vuoi salvare la bozza prima di uscire dalla cartella?</p>
              </div>
            </div>
            <div className="flex flex-col gap-2 mt-4">
              <button data-testid="exit-save-draft" onClick={handleExitSaveDraft} className="w-full bg-brand-navy hover:bg-brand-navyDark text-white font-bold py-2.5 rounded-lg text-sm flex items-center justify-center gap-2">
                Salva bozza ed esci
              </button>
              <button data-testid="exit-discard" onClick={handleExitDiscard} className="w-full bg-white border border-red-300 text-red-700 hover:bg-red-50 font-bold py-2.5 rounded-lg text-sm">
                Esci senza salvare
              </button>
              <button data-testid="exit-cancel" onClick={handleExitCancel} className="w-full text-slate-600 hover:bg-slate-100 font-semibold py-2 rounded-lg text-sm">
                Resta qui
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Conferma azioni distruttive (Elimina bozza / Archivia / Ripristina) */}
      {destructiveAction && (
        <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" data-testid="visit-destructive-confirm" onClick={(e) => { if (e.target === e.currentTarget && !destructiveBusy) setDestructiveAction(null); }}>
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl">
            <p className="text-base text-brand-navy font-semibold leading-snug" data-testid="visit-destructive-confirm-msg">
              {destructiveAction === "delete" && "Stai eliminando la bozza. Sicuro di voler procedere?"}
              {destructiveAction === "archive" && "Stai archiviando il referto. Sicuro di voler procedere?"}
              {destructiveAction === "restore" && "Stai ripristinando questa visita. Sicuro di voler procedere?"}
            </p>
            <div className="flex justify-end gap-2 mt-5">
              <button
                data-testid="visit-destructive-cancel"
                disabled={destructiveBusy}
                onClick={() => setDestructiveAction(null)}
                className="px-4 py-2 rounded-lg text-sm font-bold text-slate-600 hover:bg-slate-100 disabled:opacity-60"
              >
                Annulla
              </button>
              <button
                data-testid="visit-destructive-ok"
                disabled={destructiveBusy}
                onClick={async () => {
                  if (destructiveBusy) return;
                  setDestructiveBusy(true);
                  try {
                    // Disabilita autosave/dirty-prompt prima di navigare via
                    setDirty(false);
                    if (destructiveAction === "delete") {
                      await api.delete(`/visits/${vid}`);
                      toast.success("Bozza eliminata");
                      // forza navigazione senza prompt di uscita
                      nav("/visite", { replace: true });
                      return;
                    } else if (destructiveAction === "archive") {
                      await api.post(`/visits/${vid}/cancel`, {});
                      toast.success("Referto archiviato");
                      nav("/visite", { replace: true });
                      return;
                    } else if (destructiveAction === "restore") {
                      await api.post(`/visits/${vid}/restore`, {});
                      toast.success("Visita ripristinata");
                      // ricarica la visita per aggiornare lo stato locale
                      const { data } = await api.get(`/visits/${vid}`);
                      setV(data);
                    }
                    setDestructiveAction(null);
                  } catch (e) {
                    toast.error(apiErr(e, "Operazione non riuscita"));
                  } finally {
                    setDestructiveBusy(false);
                  }
                }}
                className="px-5 py-2 rounded-lg text-sm font-bold bg-red-600 hover:bg-red-700 text-white disabled:opacity-60"
              >
                {destructiveBusy ? "…" : "Conferma"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}


/**
 * SpecializedBlocks: card per i blocchi clinici specializzati che variano per ambulatorio.
 * Mostra un riquadro dedicato (testarea con label specifica) solo se il template include la chiave.
 */
function SpecializedBlocks({ showBlock, v, readOnly }) {
  // Helper per campi specializzati salvati in v.specialized (oggetto libero) - fallback in memoria
  const [draft, setDraft] = React.useState(() => v.specialized || {});
  const set = (k, val) => setDraft((p) => ({ ...p, [k]: val }));

  const Block = ({ k, label, placeholder, rows = 3, testid }) => {
    if (!showBlock(k)) return null;
    return (
      <div className="bg-white rounded-xl border border-[#E2E8F0] p-4" data-testid={testid || `sb-${k}`}>
        <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</label>
        <textarea
          rows={rows}
          disabled={readOnly}
          value={draft[k] || ""}
          onChange={(e) => set(k, e.target.value)}
          placeholder={placeholder}
          className="mt-1.5 w-full px-3 py-2 rounded-lg border border-[#E2E8F0] focus:outline-none focus:border-brand-azure disabled:bg-slate-50 text-sm"
        />
      </div>
    );
  };

  const blocks = [
    // Glaucoma
    { k: "gonioscopia", label: "Gonioscopia / Angolo iridocorneale", ph: "Angolo aperto/chiuso, gradazione Shaffer/Spaeth, sinechie, pigmentazione…" },
    // Cornea
    { k: "film_lacrimale", label: "Film lacrimale / BUT / Schirmer", ph: "BUT (s), Schirmer (mm/5min), MMP-9, osmolarità…" },
    { k: "superficie_oculare", label: "Superficie oculare (fluoresceina)", ph: "Staining corneale/congiuntivale (Oxford/NEI), erosioni puntate, ulcere…" },
    { k: "congiuntiva", label: "Congiuntiva / Sclera", ph: "Iperemia, follicoli, papille, episclerite/sclerite, pinguecola/pterigio…" },
    // Cataratta
    { k: "iol_choice", label: "Scelta IOL", ph: "Tipo: monofocale / multifocale / EDOF / torica; potenza; target refrazione; suggerimento IA…", rows: 4 },
    { k: "consenso_preoperatorio", label: "Consenso / Preoperatorio", ph: "Consenso informato firmato, ASA, controindicazioni assenti, preop OK…" },
    // Refrattiva
    { k: "aberrometria", label: "Aberrometria", ph: "HOA totali RMS, coma, sferica, trefoil…" },
    { k: "pupillometria", label: "Pupillometria", ph: "Diametro pupillare fotopico/mesopico/scotopico mm…" },
    // Retina/Maculopatia/Diabetica
    { k: "amsler", label: "Test di Amsler / Metamorfopsie", ph: "Centrale normale / metamorfopsie / scotoma centrale OD/OS…" },
    // Pediatrico
    { k: "anamnesi_pediatrica", label: "Anamnesi pediatrica + dati tutore", ph: "Nome tutore, sviluppo psicomotorio, parto, prematurità, screening neonatale, scolarità…", rows: 4 },
  ];

  const visible = blocks.filter((b) => showBlock(b.k));
  if (visible.length === 0) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
      {visible.map((b) => (
        <Block key={b.k} k={b.k} label={b.label} placeholder={b.ph} rows={b.rows || 3} />
      ))}
    </div>
  );
}
